import { useEffect, useState, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  api,
  formatApiError,
  Job,
  type ProviderCredentialField,
  type ProviderCapabilities,
  type ProviderDescriptor,
  type ProviderAccountSummary,
  type ProviderTransportDescriptor,
  type QualityDistribution,
  type QualityThresholdPreview,
} from '../api/client'
import { useIsAdmin } from '../auth/AuthContext'
import { AdminOnly } from '../components/AdminOnly'
import { Disclosure } from '../components/Disclosure'
import {
  DEFAULT_INBOUND_METHOD,
  DEFAULT_OUTBOUND_METHOD,
  INBOUND_METHOD_OPTIONS,
  OUTBOUND_METHOD_OPTIONS,
  legLabel,
  legSpecificityNote,
  methodValueForTransport,
  INBOUND_METHOD_VALUES,
  OUTBOUND_METHOD_VALUES,
  providerLabel,
  transportForMethodValue,
  type InboundMethod,
  type OutboundMethod,
} from '../utils/methods'
import { NOT_EVALUATED, overallWindowText } from '../utils/sla'
import {
  DERIVED_DESTINATION_DID_TRANSPORT,
  DID_COMPARE_DIGITS,
  didPrefillFromAddress,
} from '../utils/did'
import { unhealthyLegs } from '../utils/accountHealth'
import { MAIL_ACCOUNT_KIND } from '../utils/legMailbox'
import {
  accountSelectorState,
  accountSelectorValue,
  type AccountListStatus,
  type AccountSelectorState,
} from './accountSelectorState'

const ACCEPTED_TYPES = '.txt,.tiff,.tif,.pdf,.doc,.docx,.png,.gif,.jpg'

/**
 * The provider this editor assumes when the descriptor has not loaded yet, and the provider
 * a job that predates the provider columns is edited as.
 *
 * This is CONFIGURATION, not measurement, which is the whole reason a default is legitimate
 * here and forbidden on a measurement row: `jobs.outbound_provider` carries a server default
 * for exactly this, whereas `runs.outbound_provider` is nullable with none and NULL there
 * means "not recorded" forever. Nothing in this file COALESCEs a measurement to eFax.
 */
const DEFAULT_PROVIDER = 'efax'

// The eFax host list and gateway-domain list that used to be hardcoded HERE are gone. They
// were a third copy of the backend's own constants, pinned to it only by a source-text
// test, so a host this form offered could be a host the backend rejected. Both now come
// from GET /api/providers, which reads the same dataclass the send path validates against
// (backend/api/providers.py).

/** The declaration role an OUTBOUND leg's sender number holds. `did_role_for_leg`'s answer. */
const OUTBOUND_ANI_ROLE = 'outbound_ani'

/**
 * The last ten digits of a DID, or null when it carries none — the browser's copy of
 * `backend/services/did.py::normalise_did`, digit count and all (`DID_COMPARE_DIGITS`).
 *
 * FOR COMPARISON ONLY. Nothing here writes it back into the form: the stored spelling is what
 * the operator recognises and what a save must preserve.
 */
function normalisedDidDigits(raw: string | null | undefined): string | null {
  const digits = (raw ?? '').replace(/\D/g, '')
  return digits ? digits.slice(-DID_COMPARE_DIGITS) : null
}

/**
 * Whether two DIDs are the SAME NUMBER — `dids_match`, in the browser.
 *
 * False when either side carries no digits, because two absences are not an agreement. This is
 * the comparison that keeps `8336645487` and `+1 833-664-5487` from reading as two numbers: the
 * accounts page shows the canonical form and a job column holds whatever was typed, and a
 * character-by-character check would report the account's own declared number as undeclared.
 */
export function sameDidNumber(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  const a = normalisedDidDigits(left)
  const b = normalisedDidDigits(right)
  return a !== null && b !== null && a === b
}

/** One entry in the From DID select. `did` is the value a save WRITES. */
export interface OutboundAniOption {
  key: string
  /**
   * The value written on save. The STORED spelling wins whenever the job already holds the same
   * number spelled differently, for the reason the destination-DID select gives: saving must
   * never rewrite a value into a form the operator did not choose. The account's spelling is
   * used for a number the job does not yet name.
   */
  did: string
  label: string
  /** True for the number the account's MAILBOX ADDRESS implies rather than a declared row. */
  derived: boolean
}

/**
 * What the From DID select offers: the outbound account's `outbound_ani` declarations, or the
 * number DERIVED from its mailbox address when it declares none.
 *
 * THE MIRROR OF `inboundDidOptions`, on purpose and rule for rule — same role filter (a
 * declaration is per account, number AND role, so an inbound-only row is not an ANI), same
 * derived-only-where-nothing-is-declared condition, same one-list-so-the-select-and-the-effect-
 * cannot-disagree shape. `resolve_leg` applies that same precondition, which is what makes the
 * condition load-bearing rather than cosmetic: where an account DOES declare ANIs, a job DID
 * outside that set is REFUSED at send time (`LegCredentialConflict`), so offering a derived
 * number beside a declared set would hand out a choice that saves and then cannot run.
 */
export function outboundAniOptions(
  account: Pick<ProviderAccountSummary, 'dids' | 'derived_dids'> | undefined,
  storedFromDid: string,
): OutboundAniOption[] {
  const declared = (account?.dids ?? []).filter(d => d.role === OUTBOUND_ANI_ROLE)
  const derived =
    declared.length === 0
      ? (account?.derived_dids ?? []).filter(d => d.role === OUTBOUND_ANI_ROLE && !d.declared)
      : []
  const stored = (storedFromDid ?? '').trim()
  /** The stored spelling when it is the same number, otherwise the account's own. */
  const valueFor = (did: string): string => (sameDidNumber(stored, did) ? stored : did)

  return [
    ...declared.map(d => {
      const display = d.did_display || d.did
      const value = valueFor(d.did)
      return {
        key: d.id,
        did: value,
        label: value === d.did ? display : `${display} — this job spells it ${value}`,
        derived: false,
      }
    }),
    ...derived.map(d => {
      const value = valueFor(d.did)
      return {
        key: `derived:${d.role}:${d.did_normalised}`,
        did: value,
        label: `${d.did_display || d.did} — from the account's mailbox address`,
        derived: true,
      }
    }),
  ]
}

/**
 * Whether the job's stored From DID is a number the account does NOT declare.
 *
 * THIS IS THE REPORTED DEFECT, made answerable. The operator's SRFax job carried
 * `from_did='234234'` — a placeholder typed months earlier — and every send failed, correctly,
 * because the job's column named a number the account does not declare and neither side may
 * silently win. The form rendered no input for the field, so the value blocking every send was
 * invisible and unclearable; he deleted it and got the identical error.
 *
 * Compared as NUMBERS (`sameDidNumber`), so a different spelling of a declared number is not
 * reported as undeclared — that false positive would send an operator looking for a
 * misconfiguration that is not there.
 */
export function fromDidIsUndeclared(
  storedFromDid: string,
  options: readonly OutboundAniOption[],
): boolean {
  const stored = (storedFromDid ?? '').trim()
  if (!stored) return false
  return !options.some(o => sameDidNumber(o.did, stored))
}

/**
 * WHAT AN EMPTY From DID MEANS, decided here rather than left to whichever render runs last.
 *
 * EMPTY IS A REAL, SUPPORTED STATE AND NOT AN OMISSION: on an account-backed outbound leg it
 * means *send under the number the account itself declares*, which is precisely what
 * `resolve_leg` does — a single declared ANI resolves from the account when the job's column is
 * blank, and where nothing is declared the mailbox derivation supplies it. The job's column is
 * an OVERRIDE, and a job is entitled not to have one.
 *
 * So the pre-fill introduced by U28 stage 3 (write the derived ANI into the field so the
 * operator need not retype an observed value) must NOT run where the field is a select that
 * already offers that number: writing a per-job copy of a value the account supplies creates
 * exactly the disagreement `resolve_leg` refuses to resolve, and it made CLEARING THE FIELD
 * IMPOSSIBLE — the next render put the number straight back with no explanation. A field that
 * springs back is the same class of defect as one that is invisible.
 *
 * `clearedByOperator` covers the remaining case, the free-text branch: within an editing session
 * an explicit clear is a decision and is remembered, so the effect does not overrule it.
 *
 * A blank-looking value is BLANK. `'   '` is absence, which is the rule the backend settled on
 * in `_job_leg_did` after a whitespace-only column silently discarded a correct derived ANI.
 */
export type FromDidPrefill =
  | { action: 'fill'; did: string }
  | {
      action: 'keep'
      because:
        | 'job-already-names-one'
        | 'the-account-supplies-it'
        | 'operator-cleared-it'
        | 'nothing-derivable'
    }

export function fromDidPrefillDecision(input: {
  current: string
  derivedAni: string | null
  /** True when the field renders a select that already offers the account's number. */
  selectable: boolean
  clearedByOperator: boolean
}): FromDidPrefill {
  if ((input.current ?? '').trim()) return { action: 'keep', because: 'job-already-names-one' }
  // Before the cleared flag deliberately: where the account supplies the number, blank is the
  // right answer whether or not this session was the one that emptied the field, which is what
  // makes the clear survive a save and a reload rather than only the next render.
  if (input.selectable) return { action: 'keep', because: 'the-account-supplies-it' }
  if (input.clearedByOperator) return { action: 'keep', because: 'operator-cleared-it' }
  if (!input.derivedAni) return { action: 'keep', because: 'nothing-derivable' }
  return { action: 'fill', did: input.derivedAni }
}

export default function JobEditor() {
  const isAdmin = useIsAdmin()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = !!id

  const [form, setForm] = useState({
    name: '', enabled: true, metrics_enabled: false, quality_check_enabled: false,
    // The THIRD SLA component (T10.33). null means the quality dimension is INACTIVE and no
    // quality verdict is produced — not "zero", which would pass everything and read on a
    // report as if a threshold were being enforced. Withheld from the UI until the score was
    // re-scaled: a threshold over a saturated score invites an SLA that cannot fail.
    min_quality_score: null as number | null,
    // Provider is chosen BEFORE transport, per leg (G9.6), and the two legs are
    // independent: a mixed-provider job is supported (G9.8) and must not be
    // structurally forbidden by collapsing these into one field.
    outbound_provider: DEFAULT_PROVIDER,
    inbound_provider: DEFAULT_PROVIDER,
    // THE account reference per leg — the pair the send path reads
    // (backend/services/leg_credentials.py reads these first, falling back to the superseded
    // sfax_* mirror). Empty means unset. This form no longer holds the sfax_* pair at all:
    // holding it is how this editor came to show a migrated job as having no account, because
    // the migration populated these columns and the form was reading the other two.
    outbound_account_id: '',
    inbound_account_id: '',
    // W3 — the OUTBOUND leg's notification mailbox, and OPTIONAL by design. Empty is a
    // first-class, valid choice: it means a send confirmation may still arrive in the inbound
    // mailbox, which is how every eFax SMTP job works today. Additional to
    // `outbound_account_id` rather than a replacement for it — a leg may name a transport
    // account AND a mailbox, and on SRFax the two are different records owned by different
    // entities.
    outbound_mail_account_id: '',
    outbound_method: DEFAULT_OUTBOUND_METHOD as OutboundMethod,
    inbound_method: DEFAULT_INBOUND_METHOD as InboundMethod,
    destination_did: '', cover_page_text: '',
    // eAPI
    api_app_id: '', api_key: '', api_user_id: '', from_did: '',
    // W1-F — NO BRAND LITERAL HERE. This seeded eFax's own `.com` API endpoint for every job
    // whatever its provider. Inert, because only eFax declares an `api_base_url` field so
    // nothing else reads it, but a confusing literal to look at and one nudge away from being
    // read as SFax's endpoint. Empty, and PRE-FILLED from the LEG's OWN descriptor
    // (`transport.hosts`) by the effect near `outboundTransport` — which opens a new eFax job on
    // the same value this literal held, and gives no other provider eFax's URL.
    api_base_url: '',
    // SMTP
    efax_smtp_send_domain: '',
    smtp_host: '', smtp_port: 587, smtp_username: '', smtp_password: '',
    smtp_tls_validate: true, from_email: '',
    // IMAP
    imap_host: '', imap_port: 993, imap_username: '', imap_password: '',
    imap_tls_validate: true, imap_retention_days: 7,
    // eAPI Webhook
    webhook_hmac_secret: '',
    // Schedule. There is NO job-wide SLA any more: the two per-leg thresholds are
    // the only controls, and null on a leg means that leg is not evaluated.
    interval_minutes: 5, cron_expression: '',
    outbound_sla_minutes: 15 as number | null,
    inbound_sla_minutes: 15 as number | null,
    max_in_flight: 10,
  })
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [job, setJob] = useState<Job | null>(null)
  // The registry, fetched once. A failure is NOT fatal: `providers` stays empty and the
  // form falls back to the static method options, because a descriptor outage must not
  // make every job un-editable.
  const [providers, setProviders] = useState<ProviderDescriptor[]>([])
  const [providersError, setProvidersError] = useState(false)
  // True when the API served a job with a provider missing on either leg. The form falls
  // back to eFax so the job stays editable, but says so rather than letting a save
  // rewrite the stored provider silently.
  const [providerMissing, setProviderMissing] = useState(false)
  // Provider account records of EVERY kind, fetched once and filtered per leg by the leg's own
  // `account_kind`. One general list, not a per-brand one: the pinned-kind SFax alias this used
  // to read cannot serve an eFax leg or the shared `imap_mailbox` record, which is why an
  // account an eFax leg already had was invisible here.
  //
  // A failure is DISCLOSED rather than fatal (`providerAccountsStatus === 'failed'`): an empty
  // selector that cannot be told apart from "no accounts exist" is how a display outage gets read
  // as a configuration problem.
  //
  // AND SO IS "NOT YET ARRIVED". This was ONE boolean, which could say loaded or failed and
  // nothing else, so the 5-9 seconds before the list lands rendered as "loaded, and empty" — a
  // bare UUID in the selector on an edit page and a "no account of this kind exists yet" panel on
  // a create page, both asserted before anything had been read. `accountSelectorState` makes the
  // three states one decision; this holds the input to it.
  const [providerAccounts, setProviderAccounts] = useState<ProviderAccountSummary[]>(
    // Seeded from the last list this session received, so the second and every later visit paints
    // a populated selector on the first frame instead of looking broken again. Always revalidated
    // below — never shown without a fresh read being in flight.
    () => api.peekProviderAccounts() ?? []
  )
  const [providerAccountsStatus, setProviderAccountsStatus] = useState<AccountListStatus>(
    () => (api.peekProviderAccounts() ? 'loaded' : 'loading')
  )
  const [uploadStatus, setUploadStatus] = useState<string | null>(null)
  // The distribution the threshold control is read against (T10.31/T10.33). A failure is NOT
  // fatal: `distError` is rendered as "shape unavailable" and the threshold still saves,
  // because a statistics outage must not make a job un-editable.
  const [dist, setDist] = useState<QualityDistribution | null>(null)
  const [distError, setDistError] = useState(false)
  // The per-job replay (T10.32), fetched on an EXPLICIT click rather than per keystroke. A
  // POST per keypress against an admin route would fill the audit trail and the logs with
  // half-typed thresholds, and the operator wants one answer about the value they settled on.
  const [preview, setPreview] = useState<QualityThresholdPreview | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    api.listProviders()
      .then(r => setProviders(r.providers ?? []))
      .catch(() => setProvidersError(true))
    api.listProviderAccounts()
      .then(r => { setProviderAccounts(r ?? []); setProviderAccountsStatus('loaded') })
      // The list is NOT cleared on failure: a refresh that fails must not erase what is already
      // on screen, or one display outage becomes an empty selector. `failed` is what the operator
      // is told; whatever arrived earlier stays visible and stays selectable.
      .catch(() => { setProviderAccountsStatus('failed') })
  }, [])

  // The distribution for THIS job, loaded once per job. Only on edit: a job being created has
  // no history, and an endpoint call with no id would 404 for a reason that is not an error.
  useEffect(() => {
    if (!id) return
    let cancelled = false
    api.getQualityDistribution(id)
      .then(d => { if (!cancelled) { setDist(d); setDistError(false) } })
      .catch(() => { if (!cancelled) { setDist(null); setDistError(true) } })
    return () => { cancelled = true }
  }, [id])

  const runPreview = async () => {
    if (!id || form.min_quality_score === null) return
    setPreviewing(true)
    setPreviewError(null)
    try {
      setPreview(await api.previewJobQualityThreshold(id, form.min_quality_score))
    } catch (err) {
      setPreview(null)
      setPreviewError(formatApiError(err, 'Could not replay this threshold'))
    } finally {
      setPreviewing(false)
    }
  }

  useEffect(() => {
    if (id) {
      api.getJob(id).then(j => {
        setJob(j)
        setProviderMissing(
          j.outbound_provider == null || j.inbound_provider == null
        )
        setForm(prev => ({
          ...prev,
          name: j.name, enabled: j.enabled,
          metrics_enabled: j.metrics_enabled ?? false,
          quality_check_enabled: j.quality_check_enabled ?? false,
          // ?? null, not || null: 0 is a legitimate threshold ("any measurable score
          // passes") and must not be coerced into "inactive".
          min_quality_score: j.min_quality_score ?? null,
          // The provider columns are NOT NULL with a server default, and JobResponse now
          // serialises them, so a job ALWAYS reads back with a provider on both legs.
          // Absence therefore means the API did not send it — an API contract fault, not
          // an old row. The previous `|| DEFAULT_PROVIDER` treated the two as the same
          // thing, which is how opening an SFax job populated the select with eFax and a
          // save silently rewrote the job's provider.
          //
          // A fallback is still applied rather than leaving the select unset, because an
          // unset required select makes the job UN-EDITABLE — the same class of bug that
          // an unconditional credential validator caused for saved eAPI jobs once. The
          // difference is that it is now DISCLOSED (see providerMissing below) instead of
          // silently changing the stored value.
          outbound_provider: j.outbound_provider ?? DEFAULT_PROVIDER,
          inbound_provider: j.inbound_provider ?? DEFAULT_PROVIDER,
          // GENERAL pair first, sfax_* only as a fallback — the same read order as
          // backend/services/leg_credentials.py, so what this form shows is what the send
          // path will use. Reading the sfax_* pair alone is the defect: the data migration
          // populated the general columns, so a migrated job read back as having no account
          // and the form then demanded typed credentials for a leg that had an account.
          outbound_account_id: j.outbound_account_id || j.sfax_outbound_account_id || '',
          inbound_account_id: j.inbound_account_id || j.sfax_inbound_account_id || '',
          // W3 — read back with NO fallback to any other column. There is no mirror and no
          // neighbour to borrow from: an unset outbound mailbox is a real, valid state, and
          // defaulting it to `inbound_account_id` would write the inbound mailbox onto the
          // outbound leg on the first save of any job that opened this form.
          outbound_mail_account_id: j.outbound_mail_account_id || '',
          outbound_method: j.outbound_method, inbound_method: j.inbound_method,
          destination_did: j.destination_did, cover_page_text: j.cover_page_text || '',
          from_did: j.from_did || '',
          // W1-F — the stored value, or EMPTY. The eFax-`.com` fallback this replaces put one
          // brand's URL into the form for an SFax or SRFax job whose column is NULL, on a leg
          // that declares no such field. An empty field is PRE-FILLED from the leg's own
          // descriptor (see the effect near `outboundTransport`), which never overwrites what
          // was read here — so eFax opens on its stored region and nobody else opens on eFax's.
          api_base_url: j.api_base_url || '',
          efax_smtp_send_domain: j.efax_smtp_send_domain || '',
          smtp_host: j.smtp_host || '', smtp_port: j.smtp_port || 587,
          smtp_tls_validate: j.smtp_tls_validate ?? true, from_email: j.from_email || '',
          imap_host: j.imap_host || '', imap_port: j.imap_port || 993,
          imap_tls_validate: j.imap_tls_validate ?? true, imap_retention_days: j.imap_retention_days,
          interval_minutes: j.interval_minutes || 5, cron_expression: j.cron_expression || '',
          max_in_flight: j.max_in_flight,
          // ?? not ||, and NO default: null means 'this leg is not evaluated' and
          // must survive a round-trip through the editor. `|| 15` would silently
          // reimpose a threshold the operator cleared on purpose.
          outbound_sla_minutes: j.outbound_sla_minutes ?? null,
          inbound_sla_minutes: j.inbound_sla_minutes ?? null,
        }))
      })
    }
  }, [id])

  /**
   * Mirror of the backend's model_validator in backend/schemas/job.py.
   *
   * Kept deliberately in step with it, field for field. This is duplication, and
   * the alternative -- letting the server be the only validator -- is what
   * produced a save that failed with no usable message. The backend remains the
   * authority; this exists so the user is told what is missing BEFORE a round
   * trip, and so a partially filled form cannot be submitted at all.
   *
   * On EDIT, credential fields are omitted from the check: they come back masked,
   * so a blank input means "keep the stored secret" rather than "empty".
   */
  const validateForm = (): string[] => {
    const missing: string[] = []
    const need = (value: unknown, label: string) => {
      if (value === undefined || value === null || value === '') missing.push(label)
    }

    need(form.name, 'Job Name')

    // 4c — DO NOT DEMAND A DID THE SERVER DERIVES. See `inboundDidIsDerivable`, which is computed
    // once in the component body so this check and the field's own `required` attribute cannot
    // disagree: leaving `required` on while skipping the check here would trade a readable
    // "Destination DID is missing" for the browser's own silent refusal to submit.
    if (!inboundDidIsDerivable) {
      need(form.destination_did, 'Destination DID')
    }

    // AN ACCOUNT-BACKED LEG HAS NO JOB-SIDE CREDENTIALS TO CHECK. Every field in the two
    // branches below belongs to the leg's credential spec, so once that leg resolves from an
    // account record the checks are asking for values the send path will never read — and
    // demanding them is exactly what made a migrated job un-editable: the account was already
    // configured and the form insisted on retyping its secrets.
    //
    // Decided per leg from the descriptor's own `account_kind`, never from a provider slug.
    // When the registry has not loaded, both kinds are null and the credential checks apply —
    // the pre-registry behaviour, which is the safe direction: it asks for too much rather
    // than letting a genuinely job-credentialled leg through unchecked.
    const outboundUsesAccount = outboundAccountKind !== null
    const inboundUsesAccount = inboundAccountKind !== null

    // An account-backed leg must NAME its account — but only when one exists to name. With no
    // account of that kind there is nothing the operator could select, so blocking the save
    // here would turn a missing record into an unusable form with no way out; the fieldset says
    // where to create one and the API refuses the save with its own reason.
    if (outboundUsesAccount && accountsOfKind(outboundAccountKind).some(a => a.enabled)) {
      need(form.outbound_account_id, 'Outbound Account')
    }
    if (inboundUsesAccount && accountsOfKind(inboundAccountKind).some(a => a.enabled)) {
      need(form.inbound_account_id, 'Inbound Account')
    }

    if (!outboundUsesAccount && form.outbound_method === 'eapi') {
      need(form.from_did, 'From DID')
      // All THREE eAPI credentials come back MASKED on edit — App ID and User ID
      // render the same '•••••••• (saved — leave blank to keep)' placeholder as the
      // key does — and the backend's update path treats a blank credential as "keep
      // the stored value" (backend/api/jobs.py: `if value:` guards the re-encrypt).
      //
      // Requiring App ID and User ID unconditionally therefore contradicted both the
      // placeholder and the backend, and made EVERY eAPI job un-editable: no field
      // could be changed — not even attaching a document — without re-typing two
      // secrets the form deliberately refuses to echo back.
      //
      // The SMTP and IMAP branches below already gate both halves of their
      // credential pairs this way; eAPI gated only the key.
      if (!isEdit) {
        need(form.api_app_id, 'eAPI App ID')
        need(form.api_user_id, 'eAPI User ID')
        need(form.api_key, 'eAPI Key')
      }
    } else if (!outboundUsesAccount && form.outbound_method === 'smtp') {
      need(form.smtp_host, 'SMTP Host')
      need(form.smtp_port, 'SMTP Port')
      need(form.from_email, 'From Email')
      if (!isEdit) {
        need(form.smtp_username, 'SMTP Username')
        need(form.smtp_password, 'SMTP Password')
      }
    }

    if (!inboundUsesAccount && form.inbound_method === 'imap') {
      need(form.imap_host, 'IMAP Host')
      need(form.imap_port, 'IMAP Port')
      if (!isEdit) {
        need(form.imap_username, 'IMAP Username')
        need(form.imap_password, 'IMAP Password')
      }
    }

    // The backend accepts either a fixed interval or a cron expression, so this
    // cannot be expressed as `required` on either input alone.
    if (!form.interval_minutes && !form.cron_expression) {
      missing.push('either an Interval or a Cron Expression')
    }

    return missing
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate BEFORE the request. These requirements are CONDITIONAL — api_key
    // matters only in eAPI mode, smtp_password only in SMTP mode — which a static
    // HTML `required` attribute cannot express, so those inputs carried no
    // `required` at all and the browser never blocked submit. The result was a
    // silent round-trip to a backend 422.
    const missing = validateForm()
    if (missing.length > 0) {
      setError(`Please complete: ${missing.join(', ')}`)
      return
    }

    setSaving(true)
    try {
      const payload: Partial<Job> = { ...form }

      // The account references are UUID columns, and the form holds '' for "unset". An empty
      // string is NOT a valid UUID, so sending it verbatim would 422 EVERY save — including on
      // a job whose legs need no account at all, because the form always carries both keys.
      // Normalise to null, which is what "no account" actually means and what the column
      // stores. Kept out of the credential-stripping block below because these are references
      // rather than secrets: they ARE echoed back by the API, so a blank means "clear it" and
      // must be sent, not dropped.
      for (const f of ['outbound_account_id', 'inbound_account_id', 'outbound_mail_account_id'] as const) {
        if (!payload[f]) payload[f] = null
      }

      // The superseded sfax_* mirror is ECHOED, never authored — and only while it still AGREES
      // with the general column. A job the migration left with both pairs set keeps its mirror,
      // because dropping the keys would un-mirror a working job for no reason.
      //
      // When the two DISAGREE the mirror is cleared instead, and that is not housekeeping: the
      // resolver falls back to it when the general column is null, so a mirror left behind after
      // the operator repointed or cleared the leg would keep sending through the account they
      // just removed — silently, and only on the leg whose general column happened to be empty.
      // A leg configured here therefore writes the general column alone.
      for (const [general, mirror] of [
        ['outbound_account_id', 'sfax_outbound_account_id'],
        ['inbound_account_id', 'sfax_inbound_account_id'],
      ] as const) {
        if (!job?.[mirror]) continue
        payload[mirror] = payload[general] === job[mirror] ? job[mirror] : null
      }

      // Credential fields are never echoed back by the API (they're masked), so
      // on EDIT a blank field means "keep the stored value" and must be stripped
      // rather than sent as an empty string that would wipe the secret.
      //
      // On CREATE there is nothing stored to keep, so stripping is actively
      // harmful: it deleted the very fields the backend's validator requires,
      // turning "you forgot the SMTP password" into an opaque 422. Strip only
      // when editing.
      //
      // The list is now DERIVED from the registry (every field declared SECRET or
      // CLEARABLE) rather than hand-maintained. A hand-written list is one a new
      // provider's secret gets left out of, and the consequence of leaving one out is
      // that saving an unrelated change blanks a stored credential.
      //
      // The floor is a UNION, not an alternative. It used to apply only when the registry set
      // came back COMPLETELY EMPTY — which meant a set that was merely INCOMPLETE bypassed the
      // floor entirely, and an incomplete set is far likelier than an absent one: one renamed
      // `Secrecy` member, one leg dropped from a descriptor, and `webhook_hmac_secret` silently
      // stops being stripped. It is the ONLY `CLEARABLE` field, so an empty string does not mean
      // "unchanged" for it — it means DELETE THE SECRET, and the secret is what authenticates a
      // webhook URL already registered in eFax's portal and not re-registerable from here.
      // Unioning cannot mis-strip: a field named here that no provider declares is simply absent
      // from the payload, so the extra names cost nothing while a missing one costs a credential.
      if (isEdit) {
        const KNOWN_CREDENTIAL_KEYS = [
          'api_app_id', 'api_key', 'api_user_id',
          'smtp_username', 'smtp_password',
          'imap_username', 'imap_password',
          'webhook_hmac_secret',
        ]
        const credentialFields: string[] = [
          ...new Set([...registryCredentialKeys(), ...KNOWN_CREDENTIAL_KEYS]),
        ]
        for (const f of credentialFields) {
          if (!(payload as Record<string, unknown>)[f]) {
            delete (payload as Record<string, unknown>)[f]
          }
        }
      }

      if (isEdit) {
        const updated = await api.updateJob(id!, payload)
        setJob(updated)
      } else {
        const created = await api.createJob(payload)
        navigate(`/jobs/${created.id}/edit`)
        return
      }
      navigate(`/jobs/${id}`)
    } catch (err: any) {
      setError(formatApiError(err, 'Save failed'))
    } finally {
      setSaving(false)
    }
  }

  const handleFileUpload = useCallback(async (file: File) => {
    const jobId = id || job?.id
    if (!jobId) return
    setUploading(true)
    setUploadStatus(null)
    try {
      const result = await api.uploadDocument(jobId, file)
      if (result.error) {
        setUploadStatus(`❌ ${result.error}`)
      } else {
        setUploadStatus(`✅ Uploaded: ${file.name}`)
        // Refresh job data
        const updated = await api.getJob(jobId)
        setJob(updated)
      }
    } catch (err: any) {
      setUploadStatus(`❌ Upload failed: ${err.message || 'Unknown error'}`)
    } finally {
      setUploading(false)
    }
  }, [id, job?.id])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFileUpload(file)
  }, [handleFileUpload])

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFileUpload(file)
    // Reset so same file can be re-selected
    e.target.value = ''
  }

  // Fields where an EMPTY value means "not evaluated" rather than zero. Since SLA
  // thresholds became nullable, 0 is a legitimate value meaning "must be
  // instantaneous" — so a blank box must map to null, not to Number('') === 0.
  // Conflating them would turn "not evaluated" into the strictest possible SLA.
  const NULLABLE_NUMBER_FIELDS = new Set([
    'outbound_sla_minutes', 'inbound_sla_minutes',
  ])

  // T5.17 — the third caller of the ONE address→DID rule (`utils/did.ts`), the other two
  // being the data migration's `imap_mailbox` and `efax_smtp` derivations.
  //
  // Our convention is `<DID>@e2e.ccs.biz` in BOTH directions, so each address field pre-fills
  // ITS OWN leg's DID rather than a single shared one: the IMAP mailbox is named for the DID a
  // fax is RECEIVED on (`destination_did`), and the SMTP From address for the DID it is SENT
  // from (`from_did`). Pre-filling `destination_did` from `from_email` would put the sending
  // number in the field that decides where a fax is expected back — the exact
  // faxing-one-number-while-polling-another misconfiguration that can never match a run.
  //
  // Keyed on the registry's own field keys, so this is a data table and not per-provider JSX.
  const DID_PREFILL_TARGETS: Record<string, string> = {
    imap_username: 'destination_did',
    from_email: 'from_did',
  }

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    let value: string | number | boolean | null
    if (e.target.type === 'checkbox') {
      value = (e.target as HTMLInputElement).checked
    } else if (e.target.type === 'number') {
      const raw = e.target.value
      value = raw.trim() === '' && NULLABLE_NUMBER_FIELDS.has(field) ? null : Number(raw)
    } else {
      value = e.target.value
    }
    setForm(prev => {
      const next = { ...prev, [field]: value }
      const didField = DID_PREFILL_TARGETS[field]
      if (didField && typeof value === 'string') {
        // NEVER an overwrite — `didPrefillFromAddress` returns the current value untouched
        // when the operator has already typed one. A DID they chose is authoritative even
        // against a disagreeing address (design §2.5), and silently retargeting it would give
        // them no way to notice.
        //
        // Indexed through a `Record` view because `form` is a typed literal and the DID field
        // is chosen at runtime from the table above. The cast is to the INDEX, not to `any`
        // values: both fields are strings in the form state, which the assignment relies on.
        const bag = next as Record<string, unknown>
        bag[didField] = didPrefillFromAddress(
          bag[didField] as string | null | undefined,
          value,
        )
      }
      return next
    })
  }

  // ---------------------------------------------------------------------------------
  // Everything below is DERIVED from the registry descriptor. Nothing here branches on a
  // provider slug, which is the point: adding a provider adds rows to the backend
  // dataclass and this form renders it without a code change (G9.6).
  // ---------------------------------------------------------------------------------

  const providerFor = (slug: string): ProviderDescriptor | undefined =>
    providers.find(p => p.slug === slug)

  const outboundDescriptor = providerFor(form.outbound_provider)
  const inboundDescriptor = providerFor(form.inbound_provider)

  // Whether a leg reads an ACCOUNT rather than the job's own columns is decided PER LEG, from
  // that leg's own `account_kind` — see `outboundAccountKind` / `inboundAccountKind` below,
  // which are derived after the selected transport is known.
  //
  // It used to be decided by the provider-level `credential_scope === 'account'`. That flag is
  // COARSE: it collapses a provider whose legs disagree into one answer, so eFax — every one of
  // whose legs but the webhook names an account kind — never got a picker and its operators were
  // asked to retype credentials an account record already held.

  /**
   * The DIDs the INBOUND account declares it owns (T5.41).
   *
   * The destination DID is the number a fax is RECEIVED on, so it must belong to the account
   * that receives — an outbound account's ANI is a different number with a different role, and
   * offering it here would produce a job that sends to a number nothing watches.
   *
   * Empty whenever no account is selected, the account declares none, or the list failed to
   * load. In every one of those cases the field stays FREE TEXT: a select over an empty list is
   * a job nobody can configure, and "we could not load the numbers" must not become "you may
   * not set one".
   */
  const inboundAccount = form.inbound_account_id
    ? providerAccounts.find(a => a.id === form.inbound_account_id)
    : undefined

  const inboundDeclaredDids = (inboundAccount?.dids ?? []).filter(
    d => d.role === 'inbound',
  )

  /**
   * The DIDs the inbound account's own MAILBOX ADDRESS implies (U28 stage 3).
   *
   * Offered ONLY WHERE THE ACCOUNT DECLARES NONE, and that condition is not cosmetic — it is the
   * same rule `resolve_leg` applies, deliberately. Where an account DOES declare numbers, a job
   * DID outside that set is refused at resolve time (T5.13: a fax under this account's identity
   * to a number it does not own goes nowhere, and a job polling a DID its account does not
   * receive on can never match a run). Offering a derived number alongside a declared set would
   * therefore let the operator build a job that saves and then cannot run — a form that hands
   * out an invalid choice is worse than one that hands out none.
   *
   * `declared` entries are dropped as well: a derivation that merely corroborates a stored row
   * has nothing to add, and the stored row is the one with an id and the one the
   * inbound-uniqueness index knows about.
   */
  const inboundDerivedDids =
    inboundDeclaredDids.length === 0
      ? (inboundAccount?.derived_dids ?? []).filter(
          d => d.role === 'inbound' && !d.declared,
        )
      : []

  /**
   * What the Destination DID select offers: declared numbers, or the derived one when the
   * account declares nothing. One list so the auto-choose effect and the select agree by
   * construction — two lists is how a field came to offer a value the effect could not pick.
   */
  const inboundDidOptions: {
    key: string
    did: string
    label: string
    derived: boolean
  }[] = [
    ...inboundDeclaredDids.map(d => ({
      key: d.id,
      did: d.did,
      label: d.did_display || d.did,
      derived: false,
    })),
    ...inboundDerivedDids.map(d => ({
      key: `derived:${d.role}:${d.did_normalised}`,
      did: d.did,
      label: `${d.did_display || d.did} — from the mailbox address`,
      derived: true,
    })),
  ]

  // AUTO-CHOOSE ONLY WHEN THERE IS NOTHING TO CHOOSE, and only into an empty field. Overwriting
  // a destination an operator already saved would silently repoint a live job.
  //
  // A DERIVED number is auto-chosen on the same terms as a declared one, which is the point of
  // the stage: the operator should not retype a value the system observes. It is still only ever
  // written into an EMPTY field, so a DID they typed keeps winning (design §2.5).
  useEffect(() => {
    if (inboundDidOptions.length === 1 && !form.destination_did) {
      const only = inboundDidOptions[0].did
      setForm(f => (f.destination_did ? f : { ...f, destination_did: only }))
    }
  }, [inboundDidOptions, form.destination_did])

  /**
   * The ANI the OUTBOUND account's mailbox address implies, for the same reason (U28 stage 3).
   *
   * OBSERVED AND NOT INFERRED: `sender_smtp` sets the `From:` header to the account's mailbox
   * address, so this is the number an SMTP send actually goes out under. That is exactly the
   * test SFax outbound fails — its send path transmits no ANI at all, so a DID derived there
   * would be a measurement invented rather than observed — and the backend's per-kind table is
   * what decides, so no provider is named here.
   *
   * Only where the outbound account declares no ANI, matching the inbound rule and `resolve_leg`.
   */
  const outboundAccount = form.outbound_account_id
    ? providerAccounts.find(a => a.id === form.outbound_account_id)
    : undefined

  const outboundDerivedAni =
    (outboundAccount?.dids ?? []).some(d => d.role === 'outbound_ani')
      ? undefined
      : (outboundAccount?.derived_dids ?? []).find(
          d => d.role === 'outbound_ani' && !d.declared,
        )

  // PRE-FILL, NEVER AN OVERWRITE — see `fromDidPrefillDecision` and the block below
  // `outboundAccountKind`, which is where the From DID field's state is assembled. The effect
  // cannot live here: it reads whether the field renders a SELECT, and that answer needs the
  // leg's `account_kind`, which is derived further down from the selected transport.

  /** The transport descriptor for the leg's CURRENTLY SELECTED stored method value. */
  const transportOf = (
    p: ProviderDescriptor | undefined,
    leg: 'outbound' | 'inbound',
    method: string,
  ): ProviderTransportDescriptor | undefined =>
    p?.[leg].find(t => t.transport === transportForMethodValue(method))

  const outboundTransport = transportOf(outboundDescriptor, 'outbound', form.outbound_method)
  const inboundTransport = transportOf(inboundDescriptor, 'inbound', form.inbound_method)

  /**
   * THE API ENDPOINT'S DEFAULT COMES FROM THE LEG'S OWN DESCRIPTOR, never from a literal (W1-F).
   *
   * This form used to seed the eFax `.com` endpoint into `api_base_url` in two places, for
   * every job whatever its provider. Inert — only eFax declares an `api_base_url` field, so
   * nothing else reads the value — but eFax's URL sitting in an SFax job's form is one refactor
   * away from being saved, and it is a confusing thing to read while diagnosing.
   *
   * `transport.hosts` is the mechanism `optionsFor` already uses to populate this field's
   * closed-list `<select>`, so the default is drawn from the SAME published list as the options
   * rather than from a second copy of one of them. `hosts[0]` is not an assumption about tuple
   * order: `tests/test_provider_default_api_host.py` asserts, for every provider in the
   * registry, that its first published host IS the one its own send path falls back to — which
   * for eFax is the `.com` endpoint this form used to hardcode, so eFax's behaviour here is
   * unchanged, value for value.
   *
   * PRE-FILL, NEVER AN OVERWRITE AND NEVER A CLEAR — the same rule this file already applies to
   * `destination_did` and `from_did`, and here it is a correctness requirement rather than a
   * convention. The descriptors and the job load in SEPARATE effects, so on an edit the stored
   * value can be in the form before `outboundTransport` exists; an effect that rewrote a value
   * it did not recognise would silently replace an eFax job's stored region with `hosts[0]` the
   * moment the descriptor arrived — the operator opens the editor and the endpoint has changed
   * under them. So this writes ONLY into an empty field, which leaves eFax value-for-value
   * identical to the literal it replaces (a new job opens on `hosts[0]`, which is the `.com`
   * endpoint) and leaves every stored value exactly as it was read.
   *
   * A provider that does not declare the field gets NOTHING, which is the whole point: the field
   * is not rendered for it and no brand's URL is offered for it either.
   */
  useEffect(() => {
    const declares = Boolean(
      outboundTransport?.fields?.some(f => f.key === 'api_base_url'),
    )
    const hosts = (declares ? outboundTransport?.hosts : undefined) ?? []
    if (hosts.length === 0) return
    setForm(f => (f.api_base_url ? f : { ...f, api_base_url: hosts[0] }))
  }, [outboundTransport])

  /**
   * THE RULE: a leg gets an account picker WHEN ITS OWN `account_kind` IS NON-NULL.
   *
   * Per leg and per transport, from the descriptor `GET /api/providers` already publishes —
   * never from a provider slug (G9.6 forbids it) and never from the coarse provider-level
   * `credential_scope`, which cannot describe a provider whose legs disagree. `null` is a
   * STATEMENT, not an omission: eFax's inbound webhook has no account-shaped record because
   * its HMAC secret is genuinely per job, so that leg keeps its own typed field.
   */
  const outboundAccountKind = outboundTransport?.account_kind ?? null
  const inboundAccountKind = inboundTransport?.account_kind ?? null

  /* --------------------------------------------------------------------------------------
     THE OUTBOUND From DID, assembled in one place.

     The operator's report: an SRFax job carried `from_did='234234'`, a placeholder typed
     months earlier, and every outbound send failed. The refusal was CORRECT — the job's column
     named a number the account does not declare, and merging the two would fax under a number
     nobody configured — but this form rendered no input for the field at all on an
     account-backed leg, so the value blocking every send was invisible AND unclearable.
     -------------------------------------------------------------------------------------- */

  /** The account's `outbound_ani` declarations, or its derived one. See `outboundAniOptions`. */
  const outboundAniOpts = outboundAniOptions(outboundAccount, form.from_did)

  /**
   * Whether the field renders a SELECT rather than free text — the same "options or free text"
   * rule the Destination DID field uses: a select over an empty list is a job nobody can
   * configure, and "we could not load the numbers" must never become "you may not set one".
   *
   * `outboundAccountKind !== null` is the complement of the fieldset's own condition, so the
   * field is rendered by exactly one of the two surfaces and never both: where the leg reads an
   * account the picker REPLACES its credential inputs, `from_did` among them, and where it does
   * not, `ProviderLegFieldset` still renders the registry's own `from_did` field (item 3 — the
   * legacy eFax eAPI job-credentials path, which has no account to select from).
   */
  const fromDidIsSelectable = outboundAccountKind !== null && outboundAniOpts.length > 0

  /** THE `234234` CASE: stored, not declared, and until now not on screen. */
  const fromDidUndeclared = fromDidIsUndeclared(form.from_did, outboundAniOpts)

  /**
   * An explicit clear, remembered for the session so the pre-fill effect cannot overrule it.
   *
   * Only load-bearing on the free-text branch: where the account supplies the number the
   * decision does not depend on this flag at all, which is what makes a clear survive a save
   * and a reload rather than merely the next render. See `fromDidPrefillDecision`.
   */
  const [fromDidClearedByOperator, setFromDidClearedByOperator] = useState(false)

  // PRE-FILL, NEVER AN OVERWRITE — the same discipline as `didPrefillFromAddress`, applied to a
  // value read from the account rather than typed into the form. AND NEVER WHERE THE ACCOUNT
  // ITSELF SUPPLIES THE NUMBER: `fromDidPrefillDecision` carries that rule with its reasoning and
  // `tests/test_from_did_selection.py` EXECUTES it. This used to be an unconditional fill into
  // any empty field, which is what made the field unclearable.
  useEffect(() => {
    const decision = fromDidPrefillDecision({
      current: form.from_did,
      derivedAni: outboundDerivedAni?.did ?? null,
      selectable: fromDidIsSelectable,
      clearedByOperator: fromDidClearedByOperator,
    })
    if (decision.action !== 'fill') return
    const derived = decision.did
    setForm(f => (f.from_did.trim() ? f : { ...f, from_did: derived }))
  }, [outboundDerivedAni, form.from_did, fromDidIsSelectable, fromDidClearedByOperator])

  /**
   * The From DID field's onChange, for BOTH branches — the select and the legacy free text.
   *
   * An explicit clear is recorded; typing a number again clears the flag, because naming one is
   * no longer a decision to name none.
   */
  const onFromDidChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFromDidClearedByOperator(e.target.value.trim() === '')
    set('from_did')(e)
  }

  /**
   * Whether the SERVER can fill this job's destination DID, so this form must not demand it (4c).
   *
   * An IMAP inbound leg reads a `mail` account whose mailbox address IS the destination DID — its
   * local part — so `POST /api/jobs` derives an empty value from that account and refuses only when
   * nothing is derivable (`backend/services/job_did_prefill`). Demanding it here would make this
   * form the LAST surface still asking for the number, and would refuse before the API ever got the
   * chance. That is the operator's report: *"The destination DID was not pre-populated in the job
   * definition. It should have been taken by the DID configured on the SRFax IMAP inbound leg
   * definition, which would have come from the assigned email address."*
   *
   * THE SAME PAIR THE BACKEND GATES ON — an account-backed leg (`account_kind`, from the
   * descriptor, never a provider slug) on the one transport whose DID a mailbox implies. When the
   * registry has not loaded, `inboundAccountKind` is null and the field stays required, which is
   * the safe direction: it asks for too much rather than posting a job the API will 422.
   *
   * It does NOT claim a DID will be derived — the mailbox may be empty, or named rather than
   * numeric. It claims only that the SERVER is the right place to find out, because it can read the
   * account row and this form cannot read what it has not been sent.
   */
  const inboundDidIsDerivable =
    inboundAccountKind !== null &&
    transportForMethodValue(form.inbound_method) === DERIVED_DESTINATION_DID_TRANSPORT &&
    Boolean(form.inbound_account_id)

  /**
   * The legs whose account reference the BACKEND says is broken (U28).
   *
   * Read from the loaded job rather than recomputed: `GET /api/jobs/{id}` evaluated it against
   * the account table, which this form has only a summary list of. Recomputing it here would put
   * the rule in two places and let the banner and the API disagree about whether a job is broken.
   *
   * Stays populated after a failed save and clears after a successful one because `setJob` is
   * given the API's own response, and the WRITE paths evaluate health too — so the banner
   * disappears when the API agrees the repair landed, not merely because a create/update
   * response never looked at the account table.
   */
  const legsNeedingRepair = unhealthyLegs(job)

  /**
   * The accounts a leg of this kind may name.
   *
   * Filtered on `account_kind` alone — `imap_mailbox` is shared across brands because the
   * mailbox is ours, so filtering by provider would hide the one record every provider's IMAP
   * leg is supposed to use. A DISABLED account is listed but unselectable rather than removed:
   * a job already pointing at one must still show what it points at, because a select whose
   * value matches no option renders blank and saving from there would clear the reference.
   */
  const accountsOfKind = (kind: string | null): ProviderAccountSummary[] =>
    kind ? providerAccounts.filter(a => a.account_kind === kind) : []

  /**
   * The mailboxes the OUTBOUND leg's notification picker may offer (W3).
   *
   * `MAIL_ACCOUNT_KIND` and nothing else, for the reason `accountsOfKind` gives: the mailbox is
   * ours and one `mail` record is shared across brands, so filtering by the leg's provider
   * would hide the very record several providers are meant to share. It is deliberately NOT
   * derived from `outboundAccountKind` — that is the kind this leg's TRANSPORT reads
   * (`efax_api` on an eFax eAPI leg), and a notification mailbox is a `mail` account whatever
   * sends the fax.
   */
  const mailAccounts = accountsOfKind(MAIL_ACCOUNT_KIND)

  /**
   * Transport options for a leg, from the PROVIDER'S DECLARED SET — never a fixed list.
   * The option's `value` is the STORED method literal, so a descriptor-driven form still
   * writes 'eapi' / 'smtp' / 'eapi_webhook' / 'imap' and no stored value changes (G7.9).
   *
   * U27 — FILTERED TO WHAT THIS LEG CAN STORE, which the unfiltered map got wrong in both
   * directions for SRFax, because the bridge in both directions used to ignore the leg:
   *
   *   * SRFax OUTBOUND offered its send callback -> `outbound_method='eapi_webhook'`, an
   *     inbound value on an outbound column, refused by the schema's Literal. Still filtered.
   *   * SRFax INBOUND offered its inbox polling -> `inbound_method='eapi'`, likewise refused,
   *     so the real inbound leg was dropped from the menu entirely (B30).
   *
   * BOTH HALVES ARE NOW LEG-AWARE, and that is what makes the second case an OPTION rather
   * than an omission. `methodValueForTransport(t.transport, leg)` inverts inbound `api` to the
   * stored value `'api'` instead of the outbound `'eapi'`, and `legLabel(..., leg)` names the
   * transport for what it does on THIS leg — SRFax's outbound API is `Queue_Fax`, not polling.
   *
   * The storable set is `*_METHOD_VALUES` from `utils/methods`, NOT the fallback option list.
   * The fallback is the provider-agnostic menu shown before a descriptor loads and lists only
   * transports every provider has; deriving "can this column hold the value?" from it is a
   * plausible test over the wrong set, and it is what kept `'api'` filtered out.
   */
  const storableMethodValues = (leg: 'outbound' | 'inbound'): Set<string> =>
    new Set<string>(leg === 'outbound' ? OUTBOUND_METHOD_VALUES : INBOUND_METHOD_VALUES)

  const transportOptions = (
    p: ProviderDescriptor | undefined,
    leg: 'outbound' | 'inbound',
    fallback: ReadonlyArray<{ value: string; label: string }>,
  ): ReadonlyArray<{ value: string; label: string }> => {
    if (!p) return fallback
    const storable = storableMethodValues(leg)
    return p[leg]
      .map(t => ({
        value: methodValueForTransport(t.transport, leg),
        label: legLabel(p.slug, t.transport, leg),
      }))
      .filter(o => storable.has(o.value))
  }

  /**
   * FINISH-LINE A2 item 6 — the `unselectableTransports` companion to the filter above was
   * removed with the paragraph it fed. The FILTER is the load-bearing half and stays: an
   * option an operator could pick and never save is the defect U27 fixed. The reason a
   * declared transport is absent is recorded in `_NOT_JOB_SELECTABLE`
   * (tests/test_job_provider_transport_saveable.py), which fails the build on a NEW unmapped
   * pair — so the gap is still discovered, just not narrated on the form.
   */

  /**
   * Every credential key the registry declares, across all providers and transports.
   *
   * This is what the submit path strips blanks from on edit, replacing the hand-written
   * list that had to be remembered. A field the registry declares SECRET but that the list
   * forgot is exactly how a blank input wipes a stored secret — the bug the acceptance
   * test for this work reproduces.
   */
  const registryCredentialKeys = (): Set<string> => {
    const keys = new Set<string>()
    for (const p of providers) {
      for (const leg of ['outbound', 'inbound'] as const) {
        for (const t of p[leg]) {
          for (const f of t.fields) {
            if (f.secrecy === 'secret' || f.secrecy === 'clearable') keys.add(f.key)
          }
        }
      }
    }
    return keys
  }

  /**
   * Whether a value is already stored for this field, from the job's own `has_*` boolean.
   *
   * Masking and required-ness both derive from this plus `secrecy` — neither is hardcoded
   * per provider. `required` on the descriptor means "required WHEN NOT ALREADY STORED",
   * so a stored secret makes the field optional and a blank submission means KEEP.
   */
  const isStored = (f: ProviderCredentialField): boolean => {
    if (!f.presence_flag) return false
    return Boolean((job as unknown as Record<string, unknown>)?.[f.presence_flag])
  }

  if (!isAdmin) {
    return (
      <div>
        <h2 style={{ marginTop: 0 }}>Job Configuration</h2>
        <AdminOnly what="Creating and editing jobs" />
      </div>
    )
  }

  return (
    <div style={{ maxWidth: '700px' }}>
      <h2>{isEdit ? 'Edit Job' : 'New Job'}</h2>
      {error && <div style={{ background: '#3a1e1e', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem', color: '#ef9a9a', border: '1px solid #5a2a2a' }}>{error}</div>}
      {providerMissing && (
        <div style={{ background: '#3a2e1e', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem', color: '#ffa726', border: '1px solid #5a4a2a', fontSize: '0.85rem' }}>
          This job was served without a provider on one or both legs. The provider columns
          are NOT NULL with a default, so this is an API fault rather than an old row. The
          form is showing <strong>eFax</strong> so the job stays editable — check the leg
          providers below before saving, because saving will store what you see here.
        </div>
      )}

      {/* U28 — THE REPAIR BANNER, and the hard requirement of the account consolidation:
          "we can break the job as is, but we can[not] break it and not give me a UI that allows
          me to fix/reset it up."

          The per-leg account picker below has always been able to repoint a leg. What was
          missing is this: being TOLD, on opening the job, that a leg points at an account that
          is gone, disabled, or of a kind this leg cannot read. Previously the only trace was a
          `<uuid> — not in the current list` option inside the dropdown, which states the fact
          in the one place you look only if you already suspect it.

          The verdict and its wording come from the API (`*_account_health`), not from a rule
          re-derived here: judging it needs the registry's declared `account_kind` for the leg
          and the actual kind of the referenced row. `needs_attention` is already false for
          `not_evaluated`, so this cannot fire on a response that simply did not check. */}
      {legsNeedingRepair.length > 0 && (
        <div style={{ background: '#3a2e1e', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem', color: '#ffa726', border: '1px solid #5a4a2a', fontSize: '0.85rem' }}>
          <strong>
            {legsNeedingRepair.length === 1
              ? 'One leg of this job cannot use the account it names.'
              : 'Neither leg of this job can use the account it names.'}
          </strong>
          <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.2rem' }}>
            {legsNeedingRepair.map(({ leg, label, health }) => (
              <li key={leg} style={{ marginBottom: '0.3rem' }}>
                <strong>{label}:</strong> {health.detail ?? health.status}
              </li>
            ))}
          </ul>
          <p style={{ margin: '0.5rem 0 0' }}>
            Fix it in the leg&apos;s <strong>Account</strong> selector below, then save. The
            job&apos;s schedule, document and run history are untouched.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <Field label="Job Name" required><input value={form.name} onChange={set('name')} required style={inputStyle} /></Field>

        {/* PROVIDER BEFORE TRANSPORT, per leg (G9.6). Two independent pairs, because a
            job may legitimately use one provider outbound and another inbound (G9.8) —
            the warning below fires when they differ rather than the form forbidding it. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <Field label="Outbound Provider">
            <select value={form.outbound_provider} onChange={set('outbound_provider')} style={inputStyle}>
              {providers.length === 0
                ? <option value={form.outbound_provider}>{providerLabel(form.outbound_provider)}</option>
                : providers.map(p => <option key={p.slug} value={p.slug}>{p.label}</option>)}
            </select>
          </Field>
          <Field label="Inbound Provider">
            <select value={form.inbound_provider} onChange={set('inbound_provider')} style={inputStyle}>
              {providers.length === 0
                ? <option value={form.inbound_provider}>{providerLabel(form.inbound_provider)}</option>
                : providers.map(p => <option key={p.slug} value={p.slug}>{p.label}</option>)}
            </select>
          </Field>
        </div>

        {/* THE ACCOUNT PICKERS LIVE IN EACH LEG'S OWN FIELDSET, below, and REPLACE that leg's
            credential inputs rather than sitting beside them — a leg that reads an account has
            no job-side credentials to type, and rendering both invites someone to fill in
            fields the send path will never read.

            There is deliberately no shared block here any more. One pair of selectors labelled
            for a single brand, gated on the coarse provider-level flag, is what made an account
            invisible on every provider but SFax and on every SFax leg that reads our own
            mailbox. Shown per leg and never shared: eFax authenticates its inbound download
            with the OUTBOUND leg's credentials, and that coupling is exactly why eFax
            SMTP-send plus webhook-receive cannot complete — inheriting it here would reproduce
            that defect on a provider that resolves per leg. */}

        {form.outbound_provider !== form.inbound_provider && (
          <p style={{ fontSize: '0.78rem', color: '#ffa726', margin: 0, padding: '0.5rem 0.6rem', background: '#3a2e1e', borderRadius: '4px', border: '1px solid #5a4a2a' }}>
            This job sends via <strong>{providerLabel(form.outbound_provider)}</strong> and receives via{' '}
            <strong>{providerLabel(form.inbound_provider)}</strong>. That is supported and each leg is
            labelled with its own provider throughout the app, but it is usually a mistake — check it is
            deliberate. The SLA is still evaluated per this job's configuration, not per provider: one
            job, one pair of thresholds, one verdict.
          </p>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <Field label="Outbound Method">
            <select value={form.outbound_method} onChange={set('outbound_method')} style={inputStyle}>
              {transportOptions(outboundDescriptor, 'outbound', OUTBOUND_METHOD_OPTIONS)
                .map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Inbound Method">
            <select value={form.inbound_method} onChange={set('inbound_method')} style={inputStyle}>
              {transportOptions(inboundDescriptor, 'inbound', INBOUND_METHOD_OPTIONS)
                .map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        </div>

        {form.inbound_provider === 'srfax' && form.inbound_method === 'api' && (
          <p style={{ fontSize: '0.78rem', color: '#ffa726', margin: 0 }}>
            <strong>This inbound leg is configurable but does not close yet.</strong> SRFax inbox
            polling runs and stores every received fax durably, but it does not hand them to the
            inbound lane, so a run on this leg will not reach a received state and no SRFax round
            trip is measured yet (backlog B24). Nothing is lost — the faxes are in storage and the
            deferral is counted, not silent. Configure the leg now if you are preparing the job;
            do not schedule it expecting a verdict.
          </p>
        )}

        {/* FINISH-LINE A2 item 6 — the paragraph naming each declared-but-unstorable transport
            per leg was removed from the form. The FACT is kept at the two sites that implement
            it: `transportOptions` above filters options to the values this leg can STORE (which
            is why an undeclarable transport never appears), and
            `tests/test_job_provider_transport_saveable.py` `_NOT_JOB_SELECTABLE` records each
            such pair with its reason, so a NEW unmapped pair fails the build instead of
            vanishing. Help's `provider-driven transport`, `SRFax Send Callback` and
            `SRFax API (polling)` entries state both of today's cases in operator language.
            Deliberately not surfaced here: the form's job is to configure what a job CAN store,
            and the operator cannot act on a transport the provider drives itself. The wording
            avoids quoting the removed sentence, because the guard reads this file's text and a
            quoted caveat inside a comment is how a "removed" string keeps passing for present. */}

        {providersError && (
          <p style={{ fontSize: '0.78rem', color: '#ffa726', margin: 0 }}>
            The provider registry could not be loaded, so this form is showing its built-in
            fallback method list and cannot render provider-declared credential fields. The
            job is still editable; reload to try again.
          </p>
        )}

        {/* THE OUTBOUND SENDER NUMBER — the mirror of Destination DID below, and rendered
            immediately before it so the two legs read in the order a fax travels.

            ON SCREEN ONLY WHERE THE OUTBOUND LEG READS AN ACCOUNT, which is the exact complement
            of `ProviderLegFieldset`'s own condition: that fieldset replaces a leg's credential
            inputs with the account picker, and `from_did` is one of the registry fields it
            replaces (eFax's `_OUTBOUND_API_FIELDS`). So the field is rendered by exactly one of
            the two surfaces, never both and — as the operator found — never neither. */}
        {outboundAccountKind !== null && (
          <Field label="From DID (outbound sender number)">
            {fromDidIsSelectable ? (
              <>
                <select value={form.from_did} onChange={onFromDidChange} style={inputStyle}>
                  {/* BLANK IS A REAL CHOICE, so it says what it does. `resolve_leg` sends under
                      the account's own declared ANI when this column is empty; the option is not
                      an "unset" placeholder and must not read as one. */}
                  <option value="">— none: send under the account&apos;s declared number —</option>
                  {outboundAniOpts.map(d => (
                    // VALUE is the stored spelling wherever the job already holds this same
                    // number, so saving never rewrites what the operator entered; only the
                    // LABEL is canonical. See `outboundAniOptions`.
                    <option key={d.key} value={d.did}>{d.label}</option>
                  ))}
                  {fromDidUndeclared && (
                    /* THE STALE VALUE, VISIBLE AT LAST. A controlled select whose value matches
                       no option renders blank, so without this option the number that is failing
                       every send would still be off screen — and the operator would be looking
                       at an apparently empty field while the API refused the leg. */
                    <option value={form.from_did}>
                      {form.from_did} — not declared on this account
                    </option>
                  )}
                </select>
                {fromDidUndeclared ? (
                  <div style={{ fontSize: '0.72rem', color: '#ffa726', marginTop: '0.2rem' }}>
                    This job sends under <strong>{form.from_did}</strong>, which this account does
                    not declare as an outbound number — so every send on this leg is refused,
                    deliberately: a fax under the account&apos;s identity from a number it does not
                    own goes out misattributed. Pick a declared number above, or choose{' '}
                    <em>none</em> to send under the account&apos;s own. Declare another under{' '}
                    <strong>Settings → Provider Accounts</strong>.
                  </div>
                ) : (
                  <div style={{ fontSize: '0.72rem', color: '#888', marginTop: '0.2rem' }}>
                    {outboundAniOpts.some(d => d.derived) ? (
                      <>
                        Derived from the outbound account&apos;s mailbox address — an SMTP send puts
                        that address in the <code>From:</code> header, so it is the number the wire
                        actually carries. Declare one explicitly under{' '}
                        <strong>Settings → Provider Accounts</strong>.
                      </>
                    ) : (
                      <>
                        From the outbound account&apos;s declared numbers. Leaving this{' '}
                        <em>none</em> is supported and usually right: the send then uses the number
                        the account declares. Set it only to pin one of several.
                      </>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* No account selected yet, or it declares nothing derivable. FREE TEXT rather
                    than an empty select — and this branch is also what makes a stale value
                    visible before the account list has even arrived. */}
                <input
                  value={form.from_did}
                  onChange={onFromDidChange}
                  style={inputStyle}
                  placeholder="+15551234567"
                />
                <div style={{ fontSize: '0.72rem', color: '#888', marginTop: '0.2rem' }}>
                  The number this leg sends FROM. Select the outbound account above and this
                  becomes a list of the numbers that account declares. Empty is supported: the
                  send then uses the account&apos;s own declared number.
                </div>
              </>
            )}
          </Field>
        )}

        {/* T5.41 — a SELECT when the inbound account declares its numbers, free text otherwise.
            A value that is not among them is kept as an explicit option: a select whose value
            matches no option renders blank, and saving from there would silently clear the
            destination on a job that was working.

            U28 stage 3 — the options are the DECLARED numbers, or the one DERIVED from the
            account's mailbox address when it declares none. See `inboundDidOptions`. */}
        <Field label="Destination DID" required={!inboundDidIsDerivable}>
          {inboundDidOptions.length > 0 ? (
            <>
              <select value={form.destination_did} onChange={set('destination_did')} required={!inboundDidIsDerivable} style={inputStyle}>
                <option value="">— select a number —</option>
                {inboundDidOptions.map(d => (
                  // VALUE stays the stored spelling so saving never rewrites what the account declared;
                  // only the LABEL is canonical. Rendering the raw form here while the accounts page
                  // showed `+…` is how one number came to look like two.
                  <option key={d.key} value={d.did}>{d.label}</option>
                ))}
                {form.destination_did && !inboundDidOptions.some(d => d.did === form.destination_did) && (
                  <option value={form.destination_did}>
                    {form.destination_did} — not declared on this account
                  </option>
                )}
              </select>
              <div style={{ fontSize: '0.72rem', color: '#888', marginTop: '0.2rem' }}>
                {inboundDerivedDids.length > 0 ? (
                  <>
                    Derived from the inbound account's mailbox address — the local part of the
                    mailbox is the number it receives on. Declare a different one under{' '}
                    <strong>Settings → Provider Accounts</strong>.
                  </>
                ) : (
                  <>
                    From the inbound account's declared DIDs. Declare another under{' '}
                    <strong>Settings → Provider Accounts</strong>.
                  </>
                )}
              </div>
            </>
          ) : (
            <input value={form.destination_did} onChange={set('destination_did')} required={!inboundDidIsDerivable} style={inputStyle} placeholder="+15551234567" />
          )}
        </Field>

        {/* ONE component, rendered twice. There used to be four hand-written fieldsets here
            — eAPI, SMTP, eAPI Webhook, IMAP — each with its own JSX, its own placeholder
            strings and its own copy of a host list. Adding SFax would have meant four more.
            Both legs now render from the provider's declared schema in declared order, so
            the form has no per-provider branch at all (G9.6). */}
        <ProviderLegFieldset
          leg="outbound"
          provider={form.outbound_provider}
          transport={outboundTransport}
          form={form as unknown as Record<string, unknown>}
          job={job}
          set={set}
          isStored={isStored}
          capabilities={outboundDescriptor?.capabilities}
          accountKind={outboundAccountKind}
          accounts={accountsOfKind(outboundAccountKind)}
          accountField="outbound_account_id"
          accountsStatus={providerAccountsStatus}
          mailAccountField="outbound_mail_account_id"
          mailAccounts={mailAccounts}
        />

        <ProviderLegFieldset
          leg="inbound"
          provider={form.inbound_provider}
          transport={inboundTransport}
          form={form as unknown as Record<string, unknown>}
          job={job}
          set={set}
          isStored={isStored}
          capabilities={inboundDescriptor?.capabilities}
          accountKind={inboundAccountKind}
          accounts={accountsOfKind(inboundAccountKind)}
          accountField="inbound_account_id"
          accountsStatus={providerAccountsStatus}
        />

        {/* OURS, not the provider's — which is why it sits outside the provider fieldset
            rather than being smuggled into the descriptor. How long WE keep the mailbox
            copy is our retention policy; the provider has no opinion on it.

            W3 — GATED ON EITHER LEG HAVING A MAILBOX, not on the inbound method alone. The
            outbound notification mailbox is the permutation with no inbound IMAP leg at all
            (an API-plus-webhook job that still collects its confirmation by mail): gated on
            `inbound_method === 'imap'` this control simply vanished for such a job, so the
            retention applied to a mailbox it does watch was unsettable and invisible — the
            same "the engine landed, the operator surface did not" shape this project keeps
            hitting. */}
        {(form.inbound_method === 'imap' || Boolean(form.outbound_mail_account_id)) && (
          <fieldset style={{ border: '1px solid #333', borderRadius: '4px', padding: '1rem' }}>
            <legend style={{ fontSize: '0.85rem', color: '#9aa0a6' }}>Mailbox retention (ours)</legend>
            <Field label="Retention Days"><input value={form.imap_retention_days} onChange={set('imap_retention_days')} type="number" style={inputStyle} /></Field>
            <p style={{ fontSize: '0.78rem', color: '#9aa0a6', margin: '0.4rem 0 0' }}>
              How long this application keeps the mailbox copy — every mailbox this job reads,
              the outbound notification mailbox included. This is our own policy, not{' '}
              {providerLabel(form.inbound_provider)}'s.
            </p>
          </fieldset>
        )}

        {/* Schedule */}
        <fieldset style={{ border: '1px solid #333', borderRadius: '4px', padding: '1rem' }}>
          <legend style={{ fontSize: '0.85rem', color: '#9aa0a6' }}>Schedule</legend>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
            <Field label="Every (min)"><input value={form.interval_minutes} onChange={set('interval_minutes')} type="number" min={1} style={inputStyle} /></Field>
            <Field label="Cron (optional)"><input value={form.cron_expression} onChange={set('cron_expression')} style={inputStyle} placeholder="*/5 * * * *" /></Field>
            <Field label="Max In-Flight"><input value={form.max_in_flight} onChange={set('max_in_flight')} type="number" min={1} style={inputStyle} /></Field>
          </div>

          {/* SLA thresholds. There is no job-wide SLA input any more: these two ARE the
              controls, and a blank one means that leg is not evaluated. The hint below
              says so explicitly and states the derived overall window, so removing the
              old field cannot be read as "we stopped checking the overall time". */}
          <div style={{ marginTop: '0.9rem', paddingTop: '0.9rem', borderTop: '1px solid #333' }}>
            <div style={{ fontSize: '0.8rem', color: '#f5f5f5', marginBottom: '0.5rem' }}>SLA thresholds — one per leg</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <Field label="Outbound SLA (min) — submit → platform confirmed sent"><input value={form.outbound_sla_minutes ?? ''} onChange={set('outbound_sla_minutes')} type="number" min={0} style={inputStyle} placeholder={NOT_EVALUATED} /></Field>
              <Field label="Inbound SLA (min) — confirmed sent → received back"><input value={form.inbound_sla_minutes ?? ''} onChange={set('inbound_sla_minutes')} type="number" min={0} style={inputStyle} placeholder={NOT_EVALUATED} /></Field>
            </div>

            {/* The derived VALUE stays on the page; only its explanation collapses. Which half
                is which matters: "Overall window: 10 min" is a reading of this job's own
                configuration and an operator scanning the form needs it without acting, while
                "it is the sum of the two legs" is a rule they read once. */}
            <p style={{ fontSize: '0.78rem', color: '#f5f5f5', margin: '0.6rem 0 0', padding: '0.5rem 0.6rem', background: '#262626', borderRadius: '4px', border: '1px solid #333' }}>
              Overall window: <strong>{overallWindowText(form)}</strong>.{' '}
              <strong>Derived, not configured.</strong>
            </p>

            <Disclosure
              label="How the two SLA thresholds are judged, and what a blank one means"
              style={{ margin: '0.5rem 0 0' }}
              summary={<>Each leg is judged against its own threshold; a blank leg is {NOT_EVALUATED}.</>}
            >
              <p style={{ margin: 0 }}>
                The overall figure is the sum of the two legs, so with 5 and 5 the overall is 10 by
                definition. Removing the old single SLA box did not stop the overall time being
                checked; it is checked as the two legs that make it up.
              </p>
              <p style={{ margin: '0.4rem 0 0' }}>
                <strong>A run passes only if every configured leg passes.</strong> A breach on one
                leg fails the run even when the total elapsed time would have fitted inside the
                sum — 6 min outbound plus 3 min inbound fails a 5/5 job despite 9 being under 10.
              </p>
              <p style={{ margin: '0.4rem 0 0', color: '#ffa726' }}>
                <strong>Leave a leg blank and that leg is {NOT_EVALUATED}.</strong> It is not a
                pass, and there is no longer any job-wide default standing behind it — the time is
                still measured and shown, but no verdict is ever produced for that leg and it
                cannot fail the run. <code>0</code> is a real threshold meaning &quot;must be
                instantaneous&quot;, not &quot;blank&quot;.
              </p>
            </Disclosure>
          </div>

          {/* The disclosure sits OUTSIDE the <label>, not inside it beside the text it explains.
              A <button> nested in a <label> is still part of that label's activation area, so
              clicking "expand" would ALSO toggle the checkbox — a control that changes the job's
              state as a side effect of reading about it. */}
          <div style={{ marginTop: '0.9rem', paddingTop: '0.9rem', borderTop: '1px solid #333' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={set('enabled')}
                style={{ marginTop: '0.2rem' }}
              />
              <span>
                <span style={{ fontSize: '0.85rem' }}>Job enabled</span>
                <span style={{ display: 'block', fontSize: '0.72rem', color: '#9aa0a6', marginTop: '0.15rem' }}>
                  Turn this <strong>off</strong> to stop a job without deleting it. Nothing is
                  deleted and nothing is re-created.
                </span>
              </span>
            </label>
            <Disclosure
              label="What exactly happens when a job is disabled"
              style={{ marginTop: '0.3rem', paddingLeft: '1.4rem' }}
            >
              <p style={{ margin: 0 }}>
                The definition, its credentials, its document and its full run history are all
                kept, and switching it back on resumes the schedule. The scheduler stops
                dispatching new runs within one reconcile cycle (~30s) and IMAP monitoring stops
                for this job.
              </p>
              <p style={{ margin: '0.4rem 0 0' }}>
                <strong>Runs already in flight are left alone</strong> and keep being polled to
                completion, because they are picked up by run status rather than by job state —
                disabling never abandons a fax that is already out. A disabled job also stops
                emitting Prometheus series, so it cannot be mistaken for a healthy one on a
                dashboard.
              </p>
            </Disclosure>
          </div>

          <div style={{ marginTop: '0.9rem', paddingTop: '0.9rem', borderTop: '1px solid #333' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.quality_check_enabled}
                onChange={set('quality_check_enabled')}
                style={{ marginTop: '0.2rem' }}
              />
              <span>
                <span style={{ fontSize: '0.85rem' }}>Deep transmission-quality checking</span>
                <span style={{ display: 'block', fontSize: '0.72rem', color: '#9aa0a6', marginTop: '0.15rem' }}>
                  Off by default. <strong>Costs one extra page per transmission</strong>, so it
                  adds transmission time and per-page billing.
                </span>
              </span>
            </label>
            {/* The COST stays above, unconditionally: it is what the operator is agreeing to
                spend, and it belongs beside the switch rather than behind an icon. What
                collapses is the description of the measurements themselves. */}
            <Disclosure
              label="What deep quality checking measures, on and off"
              style={{ marginTop: '0.3rem', paddingLeft: '1.4rem' }}
            >
              <p style={{ margin: 0 }}>
                When <strong>off</strong>, content verification is OCR token matching only (plus
                page-count and file-size checks). When <strong>on</strong>, a calibration-target
                page is added to every outbound fax and the received copy is measured against it —
                resolution in both axes, dot-coverage fidelity, speckle and dropout, line
                continuity, skew and distortion.
              </p>
              <p style={{ margin: '0.4rem 0 0' }}>
                See <a href="/help#quality" style={{ color: '#4dd0e1' }}>Help → Quality</a>.
              </p>
            </Disclosure>
          </div>

          {/*
            T10.33 — the quality SLA threshold, with the distribution beside it.

            This control was withheld from every earlier wave on purpose. A bare threshold over
            a saturated score invites an SLA that cannot fail: when almost every fax scores at
            the ceiling, any threshold below the ceiling passes everything and reads on a report
            as if quality were being enforced. It ships now, with the re-scaled score and with
            the shape of the job's own history rendered next to it, so the number is chosen
            against evidence rather than guessed.

            Rendered only when quality checking is ON. The API refuses the pair outright
            (a threshold with scoring off could never be evaluated), so offering the field
            beside an off switch would be offering a save that cannot succeed.
          */}
          {form.quality_check_enabled && (
            <div style={{ marginTop: '0.9rem', paddingTop: '0.9rem', borderTop: '1px solid #333' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.35rem' }}>
                Minimum quality score (quality SLA)
              </label>
              <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ minWidth: '15rem' }}>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.5}
                    value={form.min_quality_score ?? ''}
                    placeholder="inactive"
                    onChange={e => setForm(f => ({
                      ...f,
                      // '' means INACTIVE, which is null — not 0. Coercing a cleared field to
                      // zero would turn "do not evaluate quality" into "any score passes",
                      // and the two produce different verdicts on every run.
                      min_quality_score: e.target.value === '' ? null : Number(e.target.value),
                    }))}
                    style={{ width: '8rem', padding: '0.4rem', background: '#1e1e1e', border: '1px solid #444', borderRadius: '4px', color: '#eee' }}
                  />
                  <span style={{ display: 'block', fontSize: '0.72rem', color: '#9aa0a6', marginTop: '0.3rem' }}>
                    Leave <strong>blank</strong> for inactive — no quality verdict is produced
                    and every existing verdict is unchanged. A value makes quality a{' '}
                    <strong>third SLA component</strong> beside the two per-leg time
                    thresholds: a received fax scoring below it breaches, and the breach counts
                    toward the batch pass ratio. Scale is 0-100.
                  </span>
                  {id && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <button
                        type="button"
                        onClick={runPreview}
                        disabled={previewing || form.min_quality_score === null}
                        style={{ padding: '0.3rem 0.75rem', background: '#2a2a2a', color: '#eee', border: '1px solid #555', borderRadius: '4px', cursor: form.min_quality_score === null ? 'not-allowed' : 'pointer', fontSize: '0.75rem' }}
                      >
                        {previewing ? 'Replaying…' : 'Preview against past runs'}
                      </button>
                      {previewError && (
                        <span style={{ display: 'block', fontSize: '0.72rem', color: '#ff8a80', marginTop: '0.3rem' }}>{previewError}</span>
                      )}
                      {preview && (
                        <div style={{ fontSize: '0.72rem', color: '#cfcfcf', marginTop: '0.4rem' }}>
                          <strong>{preview.would_breach}</strong> of{' '}
                          <strong>{preview.evaluated}</strong> scored transmission(s) would
                          breach at {preview.proposed_min_quality_score}
                          {preview.breach_delta !== null && preview.breach_delta !== undefined && (
                            <> — {preview.breach_delta === 0 ? 'no change' : `${preview.breach_delta > 0 ? '+' : ''}${preview.breach_delta}`} against the threshold in force now</>
                          )}
                          .
                          {preview.breaches.length > 0 && (
                            <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.1rem' }}>
                              {preview.breaches.slice(0, 10).map(b => (
                                <li key={b.transmission_id}>
                                  <a href={b.href} style={{ color: '#4dd0e1' }}>
                                    {b.kind === 'batch_item' ? 'batch item' : 'run'} scoring {b.quality_score}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          )}
                          {preview.notes.map((n, i) => (
                            <span key={i} style={{ display: 'block', color: '#9aa0a6', marginTop: '0.25rem' }}>{n}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/*
                  The distribution. Figures the sample cannot support arrive as null with a
                  sentence in `suppressed`, and the sentence is what gets rendered — a bar chart
                  over four faxes is the thing this whole endpoint exists to refuse.
                */}
                <div style={{ flex: '1 1 18rem', minWidth: '16rem', background: '#161616', border: '1px solid #333', borderRadius: '4px', padding: '0.6rem' }}>
                  <span style={{ display: 'block', fontSize: '0.75rem', color: '#cfcfcf', marginBottom: '0.35rem' }}>
                    This job's scores
                    {dist?.quality_score_version != null && (
                      <span style={{ color: '#9aa0a6' }}> · scoring generation {dist.quality_score_version}</span>
                    )}
                  </span>
                  {distError && (
                    <span style={{ fontSize: '0.72rem', color: '#9aa0a6' }}>
                      The distribution could not be loaded. The threshold above still saves —
                      this panel is evidence, not a dependency.
                    </span>
                  )}
                  {!distError && dist && (
                    <>
                      <div style={{ fontSize: '0.72rem', color: '#cfcfcf' }}>
                        n = <strong>{dist.count}</strong>
                        {dist.count > 0 && (
                          <>
                            {' · '}min {dist.min} · mean {dist.mean} · max {dist.max}
                            {dist.median !== null && dist.median !== undefined && <> · median {dist.median}</>}
                            {dist.p10 !== null && dist.p10 !== undefined && <> · p10 {dist.p10}</>}
                          </>
                        )}
                      </div>
                      {dist.histogram && dist.histogram.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '3.5rem', marginTop: '0.45rem' }}>
                          {dist.histogram.map(b => {
                            const peak = Math.max(...dist.histogram!.map(x => x.count)) || 1
                            return (
                              <div
                                key={b.lower}
                                title={`${b.lower}-${b.upper}: ${b.count}`}
                                style={{
                                  flex: 1,
                                  height: `${Math.max((b.count / peak) * 100, b.count > 0 ? 4 : 0)}%`,
                                  background:
                                    form.min_quality_score !== null && b.upper <= form.min_quality_score
                                      ? '#FF4133'
                                      : '#4dd0e1',
                                }}
                              />
                            )
                          })}
                        </div>
                      )}
                      {dist.histogram && (
                        <span style={{ display: 'block', fontSize: '0.68rem', color: '#9aa0a6', marginTop: '0.2rem' }}>
                          0 → 100 in bins of {dist.bin_width}. Bars fully below the threshold are
                          red — those are the transmissions it would fail.
                        </span>
                      )}
                      {dist.breaches_at_current_threshold !== null && dist.breaches_at_current_threshold !== undefined && (
                        <span style={{ display: 'block', fontSize: '0.72rem', color: '#cfcfcf', marginTop: '0.3rem' }}>
                          The threshold saved today ({dist.min_quality_score}) breaches{' '}
                          <strong>{dist.breaches_at_current_threshold}</strong> of {dist.count}.
                        </span>
                      )}
                      {dist.suppressed.map((s, i) => (
                        <span key={i} style={{ display: 'block', fontSize: '0.68rem', color: '#9aa0a6', marginTop: '0.25rem' }}>{s}</span>
                      ))}
                    </>
                  )}
                  {!distError && !dist && !id && (
                    <span style={{ fontSize: '0.72rem', color: '#9aa0a6' }}>
                      A new job has no measurements yet. Save it, let it run, then set the
                      threshold against its own history rather than against a guess.
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          <div style={{ marginTop: '0.9rem', paddingTop: '0.9rem', borderTop: '1px solid #333' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.metrics_enabled}
                onChange={set('metrics_enabled')}
                style={{ marginTop: '0.2rem' }}
              />
              <span>
                <span style={{ fontSize: '0.85rem' }}>Emit Prometheus metrics for this job</span>
                <span style={{ display: 'block', fontSize: '0.72rem', color: '#9aa0a6', marginTop: '0.15rem' }}>
                  Off by default — each enabled job adds ~11 series to <code>/metrics</code>.
                  Turning this off <strong>removes</strong> the job's series rather than
                  leaving them frozen at their last value, so a disabled job cannot be
                  mistaken for a healthy one. SLA breaches are still recorded on the run
                  either way; this only controls metrics.
                </span>
              </span>
            </label>
          </div>
        </fieldset>

        <Field label="Cover Page Text"><textarea value={form.cover_page_text} onChange={set('cover_page_text')} rows={3} style={{ ...inputStyle, resize: 'vertical' }} /></Field>

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
          <button type="button" onClick={() => navigate(-1)} style={{ padding: '0.6rem 1.5rem', border: '1px solid #555', background: '#1e1e1e', color: '#f5f5f5', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
          <button type="submit" disabled={saving} style={{ padding: '0.6rem 1.5rem', background: '#FF4133', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Job'}
          </button>
        </div>
      </form>

      {/* Document Upload Section - shown when editing an existing job */}
      {isEdit && (
        <fieldset style={{ border: '1px solid #333', borderRadius: '4px', padding: '1rem', marginTop: '1.5rem' }}>
          <legend style={{ fontSize: '0.85rem', color: '#9aa0a6' }}>Document Upload</legend>

          {job?.document_original_name && (
            <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#1e3a24', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.85rem' }}>📄 Current: <strong>{job.document_original_name}</strong></span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="button" onClick={() => api.downloadDocument(id!)} style={{ padding: '0.3rem 0.75rem', background: '#FF4133', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>Download</button>
                <button type="button" onClick={() => fileInputRef.current?.click()} style={{ padding: '0.3rem 0.75rem', background: '#ff9800', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>Replace Document</button>
              </div>
            </div>
          )}

          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? '#FF4133' : '#444'}`,
              borderRadius: '8px',
              padding: '2rem',
              textAlign: 'center',
              cursor: 'pointer',
              background: dragOver ? '#26313a' : '#262626',
              transition: 'all 0.2s',
            }}
          >
            {uploading ? (
              <p style={{ margin: 0, color: '#9aa0a6' }}>⏳ Uploading...</p>
            ) : (
              <>
                <p style={{ margin: '0 0 0.5rem', fontSize: '1.2rem' }}>📎</p>
                <p style={{ margin: '0 0 0.25rem', color: '#f5f5f5' }}>Drag & drop a file here, or click to browse</p>
                <p style={{ margin: 0, fontSize: '0.75rem', color: '#9aa0a6' }}>Supported: .txt, .tiff, .tif, .pdf, .doc, .docx, .png, .gif, .jpg</p>
              </>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />

          {uploadStatus && (
            <div style={{ marginTop: '0.75rem', padding: '0.5rem', borderRadius: '4px', background: uploadStatus.startsWith('✅') ? '#1e3a24' : '#3a1e1e', fontSize: '0.85rem' }}>
              {uploadStatus}
            </div>
          )}
        </fieldset>
      )}
    </div>
  )
}

/**
 * One leg's credential form, rendered ENTIRELY from the provider's declared schema.
 *
 * There is no `provider === 'efax'` branch anywhere in here, and that is the requirement
 * rather than a stylistic preference (G9.6): the moment a form carries per-provider JSX it
 * stops being renderable from the registry and a new provider needs a frontend change to
 * ship. Everything that used to be hardcoded is now derived:
 *
 * - the **legend** is the (provider, transport) label from `methods.ts`, so it reads
 *   "eFax SMTP Configuration" and would read "SFax API Configuration" with no edit here;
 * - the **one-line specificity note** comes from a data table, not from a conditional;
 * - **masking** derives from `secrecy` plus the job's `has_*` flag, and the CLEARABLE
 *   placeholder differs from the SECRET one because clearing a stored secret is a thing an
 *   operator legitimately needs and a shared placeholder would hide it;
 * - **required-ness** derives from `required && !stored`, which is the descriptor's own
 *   "required WHEN NOT ALREADY STORED" rule;
 * - the **API host is a closed `<select>`** over the provider's declared hosts and never a
 *   free-text input, so a host this form offers cannot be one the backend rejects.
 *
 * A capability the provider LACKS is stated as unavailable rather than left blank, because a
 * missing capability and a missing measurement look identical in an empty cell.
 */
function ProviderLegFieldset({
  leg, provider, transport, form, job, set, isStored, capabilities,
  accountKind, accounts, accountField, accountsStatus,
  mailAccountField, mailAccounts,
}: {
  leg: 'outbound' | 'inbound'
  provider: string
  transport: ProviderTransportDescriptor | undefined
  form: Record<string, unknown>
  job: Job | null
  set: (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void
  isStored: (f: ProviderCredentialField) => boolean
  capabilities?: ProviderCapabilities
  /** Non-null means THIS LEG reads an account: render the picker INSTEAD of the fields. */
  accountKind: string | null
  /** The accounts of that kind, disabled ones included so a stored reference stays visible. */
  accounts: ProviderAccountSummary[]
  /** The general column this leg writes: `outbound_account_id` / `inbound_account_id`. */
  accountField: string
  /**
   * What the account fetch has reported: `loading`, `loaded` or `failed`.
   *
   * THREE values, not the boolean this used to be. The boolean could only say "loaded" or
   * "failed", so the seconds before the list arrives were indistinguishable from "loaded and
   * empty" — and the selector said so, in the operator's words, by showing a raw GUID for 5-9
   * seconds and by telling a create page that no account of the kind exists.
   */
  accountsStatus: AccountListStatus
  /**
   * W3 — the column an OPTIONAL notification-mailbox picker writes, or undefined for a leg
   * that has none. Passed for the OUTBOUND leg only: the inbound leg's mailbox IS its
   * transport account (`accountField`), so a second picker there would be two controls over
   * one fact, and the pair could disagree.
   */
  mailAccountField?: string
  /** The `mail`-kind accounts that picker offers. Empty is a state it renders, not an error. */
  mailAccounts?: ProviderAccountSummary[]
}) {
  if (!transport) return null

  const label = legLabel(provider, transport.transport)
  const note = legSpecificityNote(provider, transport.transport)

  /** The account this leg currently names, from the GENERAL column. '' means unset. */
  const accountId = String(form[accountField] ?? '')

  /**
   * The selector's state — ONE decision, made in `accountSelectorState.ts` and executed under
   * node by `tests/test_account_selector_states.py` rather than inferred from this JSX.
   *
   * Everything below reads `selector.kind` and nothing below re-derives it from
   * `accounts.length`, which is the derivation that conflated "has not arrived" with "is empty".
   */
  const selector: AccountSelectorState = accountSelectorState(
    accountsStatus,
    accounts.map(a => a.id),
    accountId,
  )
  /** '' while loading or failed, so a bare UUID is never the selector's rendered value. */
  const selectValue = accountSelectorValue(selector, accountId)

  /* ------------------------------------------------------------------------------------
     W3 — THE OPTIONAL NOTIFICATION MAILBOX, on the leg that has one.

     Reuses the same three-valued state machine as the transport picker above rather than a
     second derivation from `mailAccounts.length`: "has not arrived" and "is empty" are
     different facts, and conflating them is what once put a raw GUID on screen for 5-9
     seconds and told a create page its configuration was empty before anything was read.

     What differs is REQUIRED-NESS, and only that. Unset is a first-class valid choice here,
     so `none-exist` is not the dead-end panel the transport picker shows — it is a disabled
     select saying no mailbox record exists yet, on a field the operator is entitled to leave
     alone.
     ------------------------------------------------------------------------------------ */
  const mailAccountId = mailAccountField ? String(form[mailAccountField] ?? '') : ''
  const mailSelector: AccountSelectorState | null = mailAccountField
    ? accountSelectorState(accountsStatus, (mailAccounts ?? []).map(a => a.id), mailAccountId)
    : null
  const mailSelectValue = mailSelector ? accountSelectorValue(mailSelector, mailAccountId) : ''

  const placeholderFor = (f: ProviderCredentialField): string => {
    if (!isStored(f)) return f.kind === 'password' ? '' : ''
    // CLEARABLE says "set", SECRET says "saved". The wording is the only signal that a
    // blank submission CLEARS on one and KEEPS on the other.
    return f.secrecy === 'clearable'
      ? '•••••••• (set — leave blank to keep, clear to remove)'
      : '•••••••• (saved — leave blank to keep)'
  }

  /** The closed option set for a `select` field, from the descriptor — never a literal. */
  const optionsFor = (f: ProviderCredentialField): string[] | null => {
    if (f.kind !== 'select') return null
    if (transport.hosts && f.key === 'api_base_url') return transport.hosts
    if (transport.gateway_domains) return transport.gateway_domains
    return []
  }

  /** ONE renderer, used by both branches below — a second copy is how two surfaces drift. */
  const renderField = (f: ProviderCredentialField) => {
    const stored = isStored(f)
    const required = f.required && !stored
    const value = form[f.key]

    if (f.kind === 'bool') {
      return (
        <Field key={f.key} label={f.label}>
          <label>
            <input type="checkbox" checked={Boolean(value)} onChange={set(f.key)} /> Yes
          </label>
        </Field>
      )
    }

    const options = optionsFor(f)
    if (options) {
      return (
        <Field key={f.key} label={f.label} required={required}>
          <select value={String(value ?? '')} onChange={set(f.key)} style={inputStyle}>
            {/* A closed set with an explicit "use the default" entry. There is no
                text input for a host or a gateway domain, deliberately. */}
            {!f.required && <option value="">(provider default)</option>}
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          {f.help && (
            <span style={{ display: 'block', fontSize: '0.72rem', color: '#9aa0a6', marginTop: '0.2rem' }}>{f.help}</span>
          )}
        </Field>
      )
    }

    return (
      <Field key={f.key} label={f.label} required={required}>
        <input
          value={String(value ?? '')}
          onChange={set(f.key)}
          type={f.kind === 'password' ? 'password' : f.kind === 'int' ? 'number' : 'text'}
          style={inputStyle}
          placeholder={placeholderFor(f)}
          autoComplete={f.kind === 'password' ? 'new-password' : undefined}
        />
        {f.help && (
          <span style={{ display: 'block', fontSize: '0.72rem', color: '#9aa0a6', marginTop: '0.2rem' }}>{f.help}</span>
        )}
      </Field>
    )
  }

  /* 4a — the fields the JOB owns even on an account-backed leg. The REGISTRY says which
     (`job_owned`), because it is not derivable: `fields` minus `account_fields` also contains
     `smtp_username` / `smtp_password` / `from_email`, which the account DOES supply (resolved
     from `mail_username` / `mail_address`) and which must stay hidden. eFax's gateway domain is
     the case that forced this — it is which gateway THIS FAX is addressed to, the send path
     reads `job.efax_smtp_send_domain`, and while the account picker replaced every field of the
     leg it had a column, a validator and no surface at all.
     *"Choosing the eFax gateway should be moved to the job edit window."* */
  const jobOwnedFields = transport.fields.filter(f => f.job_owned)

  return (
    <fieldset style={{ border: '1px solid #333', borderRadius: '4px', padding: '1rem' }}>
      <legend style={{ fontSize: '0.85rem', color: '#9aa0a6' }}>{label} Configuration</legend>

      {/* The leg's provider-specificity note. One sentence per (provider, transport) saying
          what makes this method the PROVIDER's rather than the operator's — read once per
          provider, not once per visit, so it collapses. `legSpecificityNote` still supplies it
          from the data table (G9.6): what changed is when it is on screen, not where it comes
          from. */}
      {note && (
        <Disclosure
          label={`What makes this ${label} leg specific to ${providerLabel(provider)}`}
          style={{ margin: '0 0 0.75rem' }}
          summary={<>How this leg is {providerLabel(provider)}&apos;s own, not just {transport.transport.toUpperCase()}.</>}
        >
          <p style={{ margin: 0 }}>{note}</p>
        </Disclosure>
      )}

      {accountKind !== null ? (
        /* THIS LEG READS AN ACCOUNT, so the picker REPLACES its credential inputs — the two are
           never rendered together. The fields below are the columns the account record now owns;
           offering them here would invite someone to fill in values the send path does not read
           (backend/services/leg_credentials.py resolves this leg from the account). */
        <>
          {selector.kind === 'none-exist' ? (
            /* NOT an empty select, and NOT rendered until the list has actually been read. An
               empty dropdown on a required field is a dead end that reads as a broken form, so
               this says what is missing and where it is created. The leg is left unset rather
               than unsavable — the API refuses a save that needs an account it has not got, with
               its own message.

               `none-exist` is only reachable from `loaded`: this panel used to appear during the
               5-9 second fetch, telling the operator their configuration was empty while nothing
               had been read. */
            <p style={{ fontSize: '0.78rem', color: '#ffa726', margin: 0, padding: '0.5rem 0.6rem', background: '#3a2e1e', borderRadius: '4px', border: '1px solid #5a4a2a' }}>
              No <strong>{accountKind}</strong> account exists yet, so there is nothing to select
              for this leg. Create one under <strong>Settings → Provider Accounts</strong> and
              return here. This leg&apos;s credentials live on that record, not on the job, so
              they cannot be typed in below.
            </p>
          ) : (
            <>
              <Field
                label={`${label} Account`}
                required={selector.kind === 'ready' || selector.kind === 'dangling'}
              >
                {/* THE SELECTOR IS ALWAYS ON SCREEN, including while loading — hiding it would
                    make the page reflow under the operator and lose them the field they came for.
                    What changes is what it SAYS, and it never says a UUID: `selectValue` is ''
                    until the list has been read, so the controlled select shows the explicit
                    option below rather than falling back to rendering the stored id. */}
                <select
                  value={selectValue}
                  onChange={set(accountField)}
                  style={inputStyle}
                  disabled={selector.kind === 'loading' || selector.kind === 'failed'}
                  aria-busy={selector.kind === 'loading'}
                >
                  {selector.kind === 'loading' && (
                    <option value="">Loading accounts…</option>
                  )}
                  {selector.kind === 'failed' && (
                    <option value="">Account list unavailable — reload to choose</option>
                  )}
                  {(selector.kind === 'ready' || selector.kind === 'dangling') && (
                    <>
                      <option value="">— select an account —</option>
                      {accounts.map(a => (
                        // A DISABLED account is shown and NOT selectable: it exists, so hiding it
                        // would present "you have no accounts" to someone looking at one.
                        <option key={a.id} value={a.id} disabled={!a.enabled}>
                          {a.display_name}{a.enabled ? '' : ' (disabled — enable it in Settings first)'}
                        </option>
                      ))}
                    </>
                  )}
                  {selector.kind === 'dangling' && (
                    // A stored reference the LOADED list does not contain — a deleted record or a
                    // different kind. Kept as an explicit option because a select whose value
                    // matches no option renders BLANK, and saving from there would silently clear
                    // the account a working job depends on. It is LABELLED rather than left as a
                    // bare id: the id alone was what read as broken, and it read as broken most
                    // often when nothing was wrong at all (the list was merely still loading).
                    <option value={selector.storedId}>
                      Missing account — {selector.storedId} (no longer in the list)
                    </option>
                  )}
                </select>
              </Field>

              {/* THREE STATES, THREE DIFFERENT THINGS SAID. Transient, needs-repair, and
                  display-outage are what the operator has to tell apart: the first needs nothing
                  from them, the second needs them to fix the job, the third needs them to reload
                  and NOT to save. */}
              {selector.kind === 'loading' && (
                <p style={{ fontSize: '0.78rem', color: '#9aa0a6', margin: '0.5rem 0 0' }} role="status">
                  Loading accounts… This is the list being fetched, not a configuration problem.
                  The account this job already uses is unchanged and stays saved.
                </p>
              )}
              {selector.kind === 'dangling' && (
                <p style={{ fontSize: '0.78rem', color: '#ffa726', margin: '0.5rem 0 0' }}>
                  This leg names an account that is <strong>not in the loaded list</strong> — it
                  was deleted, or it is of another kind. The list HAS loaded, so this will not
                  resolve on its own: pick a replacement and save, or recreate the record under{' '}
                  <strong>Settings → Provider Accounts</strong>. Until then this leg cannot
                  authenticate.
                </p>
              )}
              {selector.kind === 'failed' && (
                <p style={{ fontSize: '0.78rem', color: '#ef5350', margin: '0.5rem 0 0' }}>
                  The account list could not be loaded, so this selector cannot show what is
                  available. That is a display failure, not a configuration one — reload before
                  concluding no account exists, and do not save over a reference you cannot
                  currently see. The stored reference itself is untouched.
                </p>
              )}

              <Disclosure
                label="Where this leg's credentials live"
                style={{ margin: '0.6rem 0 0' }}
                summary={<>Authenticates with the selected account&apos;s stored credentials — nothing to type here.</>}
              >
                <p style={{ margin: 0 }}>
                  Those credentials are shared with every other job that names this account.
                  Manage them under <strong>Settings → Provider Accounts</strong>; changing them
                  there changes them for every job pointed at the same record.
                </p>
              </Disclosure>
            </>
          )}
          {/* 4a — AND THESE STILL BELONG TO THE JOB. Rendered alongside the picker, not
              instead of it: the account supplies the credentials, the job says which gateway
              this fax is addressed to. Empty for every leg that declares no `job_owned`
              field, so no other leg gains a control it has no column for. */}
          {jobOwnedFields.length > 0 && (
            <div style={{ marginTop: '0.9rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem' }}>
                {jobOwnedFields.map(renderField)}
              </div>
              <Disclosure
                label="Why this field is set per job rather than on the account"
                style={{ margin: '0.5rem 0 0' }}
                summary={<>Set per job, not on the account.</>}
              >
                <p style={{ margin: 0 }}>
                  A mailbox can submit to more than one gateway, and several jobs share one
                  account. Leave it on the provider default unless you are deliberately measuring
                  a specific gateway.
                </p>
              </Disclosure>
            </div>
          )}
        </>
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem' }}>
        {transport.fields.map(renderField)}
      </div>
      )}

      {mailAccountField && mailSelector && (
        /* THE OUTBOUND NOTIFICATION MAILBOX (W3), BESIDE this leg's transport configuration
           and never instead of it. A leg may name a transport account AND a mailbox: naming
           one does not displace a webhook or a status poll, and where both a mail
           notification and a callback arrive, both times are recorded — the delta between
           them is the measurement, which is why neither path is allowed to suppress the
           other.

           OPTIONAL, AND LEAVING IT EMPTY IS NOT AN INCOMPLETE FORM. `validateForm` does not
           require it, there is no asterisk, and the empty option states what happens instead
           rather than reading as a blank waiting to be filled: an unset value means a send
           confirmation may still arrive in the INBOUND mailbox, which is how every eFax SMTP
           job works today. Making this required — or defaulting it from the inbound leg —
           would break the one shape that already works, and it would look like a provider
           fault rather than ours. */
        <div style={{ marginTop: '0.9rem', paddingTop: '0.9rem', borderTop: '1px solid #333' }}>
          <Field label="Outbound notification mailbox (optional)">
            <select
              value={mailSelectValue}
              onChange={set(mailAccountField)}
              style={inputStyle}
              disabled={mailSelector.kind === 'loading' || mailSelector.kind === 'failed'}
              aria-busy={mailSelector.kind === 'loading'}
            >
              {mailSelector.kind === 'loading' && <option value="">Loading accounts…</option>}
              {mailSelector.kind === 'failed' && (
                <option value="">Account list unavailable — reload to choose</option>
              )}
              {mailSelector.kind === 'none-exist' && (
                <option value="">— none — no mail account exists yet</option>
              )}
              {(mailSelector.kind === 'ready' || mailSelector.kind === 'dangling') && (
                <>
                  {/* The EMPTY option says what unset MEANS. "— select an account —" would
                      read as an unfinished field; this reads as a decision. */}
                  <option value="">— none — look in the inbound mailbox</option>
                  {(mailAccounts ?? []).map(a => (
                    <option key={a.id} value={a.id} disabled={!a.enabled}>
                      {a.display_name}{a.enabled ? '' : ' (disabled — enable it in Settings first)'}
                    </option>
                  ))}
                </>
              )}
              {mailSelector.kind === 'dangling' && (
                // Kept as an explicit option for the transport picker's reason: a select whose
                // value matches no option renders BLANK, and saving from there would silently
                // clear a mailbox the job depends on.
                <option value={mailSelector.storedId}>
                  Missing account — {mailSelector.storedId} (no longer in the list)
                </option>
              )}
            </select>
          </Field>

          {mailSelector.kind === 'dangling' && (
            <p style={{ fontSize: '0.78rem', color: '#ffa726', margin: '0.5rem 0 0' }}>
              This leg names a mailbox that is <strong>not in the loaded list</strong> — it was
              deleted, or it is of another kind. Pick a replacement, or choose{' '}
              <strong>none</strong> to go back to looking in the inbound mailbox.
            </p>
          )}
          {mailSelector.kind === 'failed' && (
            <p style={{ fontSize: '0.78rem', color: '#ef5350', margin: '0.5rem 0 0' }}>
              The account list could not be loaded, so this selector cannot show what is
              available. That is a display failure, not a configuration one — the stored
              reference is untouched.
            </p>
          )}

          <Disclosure
            label="When to name a mailbox here, and what leaving it empty means"
            style={{ margin: '0.6rem 0 0' }}
            summary={<>Leave it empty unless this provider mails its send confirmation somewhere else.</>}
          >
            <p style={{ margin: 0 }}>
              Some providers confirm a send by email to the SAME mailbox their received faxes
              arrive in — eFax and SFax do — and those jobs need nothing here. SRFax sends its
              confirmation to a DIFFERENT mailbox, often owned by a different party, and that is
              what this field is for. Naming the same mailbox as the inbound leg is also valid
              and is not double-counted. This never replaces a webhook or a status poll: where
              both arrive, both times are recorded, and the configured leg method still decides
              which one is scored.
            </p>
          </Disclosure>
        </div>
      )}

      {accountKind === null && job && transport.fields.some(f => isStored(f)) && (
        <Disclosure
          label="What happens to a masked credential field you leave blank"
          style={{ margin: '0.6rem 0 0' }}
          summary={<><strong>Leave a masked field blank and the stored value is kept.</strong></>}
        >
          <p style={{ margin: 0 }}>
            Credentials already stored for this leg are shown masked rather than echoed back.
            Saving an unrelated change such as the job name never wipes a secret — only typing a
            new value into a masked field replaces the stored one.
          </p>
        </Disclosure>
      )}

      {capabilities && <CapabilityNotes leg={leg} provider={provider} capabilities={capabilities} />}
    </fieldset>
  )
}

/**
 * States what this provider CANNOT do, rather than rendering a blank where the fact would go.
 *
 * "Pod attribution: not available on this provider" is a different fact from a missing
 * measurement, and an empty cell cannot tell an operator which one they are looking at
 * (G9.6). Only the absences are listed — a capability the provider HAS needs no commentary.
 */
function CapabilityNotes({
  leg, provider, capabilities,
}: { leg: 'outbound' | 'inbound'; provider: string; capabilities: ProviderCapabilities }) {
  const gaps: string[] = []
  if (leg === 'outbound') {
    if (!capabilities.polls_outbound_status) {
      gaps.push('the outbound leg cannot be closed by polling a per-fax status — this provider does not expose one')
    }
    // FINISH-LINE A2 item 4 — `has_provider_accept_timestamp` is deliberately NOT surfaced here.
    // The FACT is unchanged and still stated where it is actionable: the provenance code on the
    // row itself (`our_clock_eapi_accept`, `our_clock_sfax_accept`, `our_clock_srfax_accept`)
    // says submit-side timing is ours alone FOR THAT MEASUREMENT, and Help's "Timestamp
    // provenance" section explains why the provider's own accept time does not exist on that
    // transport. A caveat on the CONFIGURATION form could only repeat it before any measurement
    // existed; a rung-3 code on the run is the same fact attached to a real reading.
    if (!capabilities.pod_attribution) {
      gaps.push('pod attribution is not available on this provider')
    }
  } else {
    if (capabilities.webhook_scope && capabilities.webhook_scope !== 'job') {
      gaps.push(`inbound callbacks are configured per ${capabilities.webhook_scope}, not per job, so this URL is not job-specific`)
    }
    if (!capabilities.inbound_email_documented) {
      gaps.push('inbound fax by email is not documented by this provider, so the mailbox grammar is provisional')
    }
  }

  if (gaps.length === 0) return null

  // COLLAPSED, BUT NOT BLANK. G9.6's point is that "not available on this provider" is a
  // different fact from a missing measurement and must be STATED rather than left as an empty
  // cell. The headline still states it unconditionally — the trigger's own summary names the
  // provider and counts the gaps — and only the list of which capabilities they are collapses.
  // An operator scanning the form still learns there is something this provider cannot do.
  return (
    <Disclosure
      tone="caution"
      style={{ margin: '0.6rem 0 0' }}
      label={`Which ${gaps.length === 1 ? 'capability is' : 'capabilities are'} not available on ${providerLabel(provider)} for the ${leg} leg`}
      summary={
        <>
          <strong>Not available on {providerLabel(provider)}:</strong>{' '}
          {gaps.length === 1 ? 'one capability' : `${gaps.length} capabilities`} on this leg.
        </>
      }
    >
      <p style={{ margin: 0 }}>{gaps.join('; ')}.</p>
    </Disclosure>
  )
}

function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '0.8rem', color: '#9aa0a6', marginBottom: '0.25rem' }}>
        {label}{required && <span style={{ color: '#ef5350' }}> *</span>}
      </label>
      {children}
    </div>
  )
}

const inputStyle: React.CSSProperties = { width: '100%', padding: '0.5rem', background: '#1e1e1e', color: '#f5f5f5', border: '1px solid #333', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' }
